import { useState, useRef, useEffect, useLayoutEffect, useCallback, useMemo, memo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion, type AnimationDefinition } from 'framer-motion';
import { RoomAudioRenderer, StartAudio } from '@livekit/components-react';
import { useAuth } from '@/hooks/useAuth';
import { ConnectionProvider, useConnection } from '@/hooks/useConnection';
import { UserNameProvider } from '@/hooks/useUserName';
import { useUserSettings } from '@/hooks/useUserSettings';
import { getAppConfig } from '@/lib/utils';
import { SessionView } from '@/components/app/session-view';
import { Toaster } from '@/components/livekit/toaster';
import { StatusBadge } from './components/StatusBadge';
import CandidateManagement from './CandidateManagement';
import {
  useProjects,
  useProject,
  useDesign,
  useCandidates,
  useParticipants,
  useResults,
  useInsights,
  useAIGenerate,
  useAIChat,
} from '@/hooks/useDashboardData';
import type { DesignWithStats, LangfusePromptConfig } from '@/types';
// Keep mock data as fallback for features not yet fully integrated
import {
  type TemplateQuestion,
} from './data/mockData';
import {

  Users,
  Settings,
  Plus,
  Edit3,
  Trash2,
  BarChart3,
  Layout,
  Send,
  X,
  ChevronRight,
  Download,
  Sparkles,
  LogOut,
  Search,
  MoreHorizontal,
  FileText,
  PlayCircle,
  Brain,
  Clock,
  Filter,
  UserPlus,
  Lightbulb,
  TrendingUp,
  Wand2,
  GripVertical,
  User,
  MessageSquare,
  ArrowRight,
  Bot,
  AlertCircle,
  Check,
  Layers,
  Loader2,
  ExternalLink,
  Copy,
  Mail,
} from 'lucide-react';
import { resultsService, designsService, aiService } from '@/services/api';
import type { InterviewResult } from '@/types';

type ViewType =
  | 'list'
  | 'detail'
  | 'template'
  | 'result'
  | 'test_play'
  | 'project_insight'
  | 'insight'
  | 'participants_list'
  | 'candidates_list'
  | 'settings';

type TabType = 'projects' | 'participants' | 'candidates' | 'settings';

// Generation parameters type
interface GenParams {
  target: string;
  topic: string;
  goal: string;
  usage: string;
}

// Memoized component for generation parameters form inputs
// This prevents the input fields from losing focus when parent re-renders
const GenParamsForm = memo(function GenParamsForm({
  genParams,
  setGenParams,
  t,
}: {
  genParams: GenParams;
  setGenParams: React.Dispatch<React.SetStateAction<GenParams>>;
  t: (key: string) => string;
}) {
  return (
    <div className="space-y-3">
      <div>
        <label className="block text-xs font-medium text-slate-600 mb-1">{t('dashboard.targetLabel')}</label>
        <textarea
          value={genParams.target}
          onChange={(e) => setGenParams(prev => ({ ...prev, target: e.target.value }))}
          placeholder={t('dashboard.targetPlaceholder')}
          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 resize-none"
          rows={2}
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-slate-600 mb-1">{t('dashboard.topicLabel')}</label>
        <textarea
          value={genParams.topic}
          onChange={(e) => setGenParams(prev => ({ ...prev, topic: e.target.value }))}
          placeholder={t('dashboard.topicPlaceholder')}
          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 resize-none"
          rows={2}
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-slate-600 mb-1">{t('dashboard.goalLabel')}</label>
        <textarea
          value={genParams.goal}
          onChange={(e) => setGenParams(prev => ({ ...prev, goal: e.target.value }))}
          placeholder={t('dashboard.goalPlaceholder')}
          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 resize-none"
          rows={2}
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-slate-600 mb-1">{t('dashboard.usageLabel')}</label>
        <textarea
          value={genParams.usage}
          onChange={(e) => setGenParams(prev => ({ ...prev, usage: e.target.value }))}
          placeholder={t('dashboard.usagePlaceholder')}
          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 resize-none"
          rows={2}
        />
      </div>
    </div>
  );
});

export function AIInterviewManager() {
  const { t, i18n } = useTranslation();
  const { locale } = useParams<{ locale: string }>();
  const { user, logout } = useAuth();

  // Generate user initials from displayName or email
  const userInitials = useMemo(() => {
    if (user?.displayName) {
      // Get initials from display name (e.g., "John Doe" -> "JD")
      const parts = user.displayName.trim().split(/\s+/);
      if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
      }
      return parts[0].substring(0, 2).toUpperCase();
    }
    if (user?.email) {
      // Get first two characters of email before @ (e.g., "john@example.com" -> "JO")
      const emailName = user.email.split('@')[0];
      return emailName.substring(0, 2).toUpperCase();
    }
    return '??';
  }, [user?.displayName, user?.email]);

  // Data hooks
  const { interviewerName, updateSettings: updateUserSettings, loading: userSettingsLoading } = useUserSettings();
  const { projects, loading: projectsLoading, error: projectsError, createProject, deleteProject: _deleteProject, refetch: _refetchProjects } = useProjects();
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const { project: selectedProject, designs, loading: projectLoading, createDesign, refetch: refetchProject } = useProject(selectedProjectId);
  const { candidates, loading: candidatesLoading, searchCandidates: _searchCandidates, createCandidate, refetch: refetchCandidates } = useCandidates();
  const { participants, loading: participantsLoading, addParticipants, refetch: refetchParticipants } = useParticipants(selectedProjectId);
  const { results, loading: resultsLoading, getResult: _getResult } = useResults(selectedProjectId);
  const { insights, loading: insightsLoading, generating: insightsGenerating, generateInsights } = useInsights(selectedProjectId);
  const { generateQuestions, generatedQuestions: generatedQuestionsAI, loading: aiLoading, clearGenerated: _clearGenerated } = useAIGenerate();
  const { messages: chatMessages, sendMessage, loading: chatLoading, clearChat: _clearChat } = useAIChat(selectedProjectId);

  // Design details hook
  const [selectedDesignIdState, setSelectedDesignIdState] = useState<string | null>(null);
  const { design: selectedDesign, questions: designQuestions, loading: designLoading, addQuestion, updateDesign, deleteDesign, refetch: refetchDesign } = useDesign(selectedDesignIdState);

  // Helper to set selected design - forces refetch even if same design
  const setSelectedDesign = (design: DesignWithStats | null) => {
    const newId = design?.id || null;
    console.log('setSelectedDesign called:', { designName: design?.name, newId, currentId: selectedDesignIdState });
    if (newId === selectedDesignIdState && newId !== null) {
      // Same design selected - force refetch
      console.log('Same design - refetching');
      refetchDesign();
    } else {
      console.log('Setting new design ID:', newId);
      setSelectedDesignIdState(newId);
    }
  };

  // UI State
  const [viewMode, setViewMode] = useState<ViewType>('candidates_list');
  const [activeTab, setActiveTab] = useState<TabType>('candidates');
  const [settingsTab, setSettingsTab] = useState<'general' | 'account'>('general');
  const [_selectedDesignId, _setSelectedDesignId] = useState<string | null>(null);
  const [activeDesignId, setActiveDesignId] = useState<string | null>(null);
  const [insightTab, setInsightTab] = useState<'report' | 'chat'>('report');

  // Track view changes to prevent animation flickering
  const prevViewModeRef = useRef<ViewType>(viewMode);
  const viewChanged = prevViewModeRef.current !== viewMode;
  useEffect(() => {
    prevViewModeRef.current = viewMode;
  }, [viewMode]);

  // Animation class - only animate when view actually changes
  const viewAnimationClass = viewChanged ? 'animate-in fade-in duration-500' : '';
  const [isAddParticipantModalOpen, setIsAddParticipantModalOpen] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isCreateDesignModalOpen, setIsCreateDesignModalOpen] = useState(false);
  const [isCreateProjectModalOpen, setIsCreateProjectModalOpen] = useState(false);
  const [isCreateCandidateModalOpen, setIsCreateCandidateModalOpen] = useState(false);
  const [isDeleteDesignModalOpen, setIsDeleteDesignModalOpen] = useState(false);
  const [designToDelete, setDesignToDelete] = useState<DesignWithStats | null>(null);
  const [deletingDesign, setDeletingDesign] = useState(false);
  const [targetDesignId, setTargetDesignId] = useState<string | null>(null);
  const [copiedLinkId, setCopiedLinkId] = useState<string | null>(null);
  const [templateQuestions, setTemplateQuestions] = useState<TemplateQuestion[]>([]);
  const [selectedCandidates, setSelectedCandidates] = useState<string[]>([]);
  const [selectedResultId, setSelectedResultId] = useState<string | null>(null);
  const [selectedResult, setSelectedResult] = useState<InterviewResult | null>(null);
  const [resultLoading, setResultLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);

  // Ref to persist genParams values across TemplateEditorView recreations
  // (TemplateEditorView is a function defined inside this component, so it gets recreated on re-render)
  // The ref holds the current values, and the state inside TemplateEditorView syncs with this ref
  const genParamsRef = useRef<GenParams>({
    target: '',
    topic: '',
    goal: '',
    usage: '',
  });

  // Track which design the genParamsRef is for, to know when to reset
  const genParamsDesignIdRef = useRef<string | null>(null);

  // Refs to preserve scroll position in template editor
  const templateEditorScrollRef = useRef<HTMLDivElement>(null);
  const templateEditorScrollPosRef = useRef(0);

  // Save scroll position on scroll
  const handleTemplateEditorScroll = useCallback(() => {
    if (templateEditorScrollRef.current) {
      templateEditorScrollPosRef.current = templateEditorScrollRef.current.scrollTop;
    }
  }, []);

  // Restore scroll position after render (synchronously before paint)
  useLayoutEffect(() => {
    if (templateEditorScrollRef.current && viewMode === 'template') {
      templateEditorScrollRef.current.scrollTop = templateEditorScrollPosRef.current;
    }
  });

  // Sync genParamsRef with design when design ID changes (load from database)
  // This ref is used to persist values across TemplateEditorView recreations
  useEffect(() => {
    // Only sync ref when switching to a different design
    if (selectedDesign?.id !== genParamsDesignIdRef.current) {
      genParamsDesignIdRef.current = selectedDesign?.id || null;
      if (selectedDesign?.generationParams) {
        genParamsRef.current = {
          target: selectedDesign.generationParams.target || '',
          topic: selectedDesign.generationParams.topic || '',
          goal: selectedDesign.generationParams.goal || '',
          usage: selectedDesign.generationParams.usage || '',
        };
      } else {
        // Reset to empty if no stored params
        genParamsRef.current = {
          target: '',
          topic: '',
          goal: '',
          usage: '',
        };
      }
    }
  }, [selectedDesign?.id, selectedDesign?.generationParams]);

  // Compute loading state for global loading modal
  const isDataLoading = projectLoading || designLoading || participantsLoading || resultsLoading || insightsLoading || resultLoading;

  // Determine what's currently loading for the message
  const isJapanese = i18n.language === 'ja';
  const getLoadingMessage = useCallback(() => {
    if (projectLoading) return isJapanese ? 'プロジェクトを読み込み中...' : 'Loading project...';
    if (designLoading) return isJapanese ? 'デザインを読み込み中...' : 'Loading design...';
    if (participantsLoading) return isJapanese ? '参加者を読み込み中...' : 'Loading participants...';
    if (resultsLoading) return isJapanese ? '結果を読み込み中...' : 'Loading results...';
    if (insightsLoading) return isJapanese ? 'インサイトを読み込み中...' : 'Loading insights...';
    if (resultLoading) return isJapanese ? '結果詳細を読み込み中...' : 'Loading result details...';
    return isJapanese ? '読み込み中...' : 'Loading...';
  }, [projectLoading, designLoading, participantsLoading, resultsLoading, insightsLoading, resultLoading, isJapanese]);

  // Sync template questions with design questions when a design is selected
  useEffect(() => {
    if (designQuestions && designQuestions.length > 0) {
      setTemplateQuestions(designQuestions.map((q, idx) => ({
        id: q.order ?? idx + 1,
        text: q.text,
        type: q.type || 'Open',
        required: q.required || false,
      })));
    } else {
      setTemplateQuestions([]);
    }
  }, [designQuestions]);

  // Fetch selected result details when result is selected
  // Auto-triggers analysis if transcript exists but no analysis has been done
  useEffect(() => {
    if (selectedResultId) {
      setResultLoading(true);
      setAnalyzing(false);

      resultsService.getById(selectedResultId)
        .then(async ({ result }) => {
          setSelectedResult(result);
          setResultLoading(false);

          // Check if we need to auto-analyze:
          // - Has transcript with content
          // - No detailed report yet OR has old format (no score field)
          const hasTranscript = result.transcript && result.transcript.length > 0;
          const needsAnalysis = !result.detailedReport || result.detailedReport.score === undefined;

          if (hasTranscript && needsAnalysis) {
            console.log('[Auto-analyze] Starting automatic analysis for result:', selectedResultId);
            setAnalyzing(true);

            try {
              await aiService.analyzeTranscript({ resultId: selectedResultId });
              console.log('[Auto-analyze] Analysis complete, refreshing result');

              // Refresh the result with new analysis
              const { result: updatedResult } = await resultsService.getById(selectedResultId);
              setSelectedResult(updatedResult);
            } catch (err) {
              console.error('[Auto-analyze] Analysis failed:', err);
              // Keep showing the result even if analysis failed
            } finally {
              setAnalyzing(false);
            }
          }
        })
        .catch((err) => {
          console.error('Failed to load result:', err);
          setSelectedResult(null);
          setResultLoading(false);
        });
    } else {
      setSelectedResult(null);
    }
  }, [selectedResultId]);

  const language = i18n.language === 'ja' ? 'ja' : 'en';

  // Helper function to format timestamps that might be Firestore timestamps, Date, or string
  const formatDate = (timestamp: { seconds: number } | Date | string | null | undefined): string => {
    if (!timestamp) return '-';
    if (typeof timestamp === 'string') {
      return new Date(timestamp).toLocaleDateString();
    }
    if (timestamp instanceof Date) {
      return timestamp.toLocaleDateString();
    }
    if ('seconds' in timestamp) {
      return new Date(timestamp.seconds * 1000).toLocaleDateString();
    }
    return '-';
  };

  const toggleLanguage = () => {
    i18n.changeLanguage(language === 'ja' ? 'en' : 'ja');
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  // ==================== VIEWS ====================

  // Project List View - Reference lines 636-687
  const ProjectListView = () => (
    <div className={`space-y-6 ${viewAnimationClass}`}>
      <div className="flex justify-between items-end">
        <div>
          <h2 className="text-lg font-bold text-slate-800">{t('dashboard.projectListTitle')}</h2>
          <p className="text-xs text-slate-500 mt-1">{t('dashboard.projectListSubtitle')}</p>
        </div>
        <button
          onClick={() => setIsCreateProjectModalOpen(true)}
          className="flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold px-4 py-2 rounded-lg transition-colors shadow-sm"
        >
          <Plus size={16} /> {t('dashboard.createProject')}
        </button>
      </div>

      {projectsLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin w-8 h-8 border-2 border-slate-300 border-t-slate-800 rounded-full"></div>
        </div>
      ) : projectsError ? (
        <div className="text-center py-12 text-red-500">
          <AlertCircle className="w-8 h-8 mx-auto mb-2" />
          <p>{t('dashboard.errorLoadingProjects')}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {projects.map((project) => (
            <div
              key={project.id}
              onClick={() => {
                setSelectedProjectId(project.id);
                setViewMode('detail');
              }}
              className="group bg-white p-6 rounded-xl border border-slate-200 shadow-sm hover:border-slate-300 hover:shadow-md transition-all cursor-pointer relative overflow-hidden"
            >
              <div className="flex justify-between items-start mb-4">
                <span className="text-[10px] font-bold text-slate-500 bg-slate-100 px-2 py-1 rounded border border-slate-200">{project.category}</span>
                <StatusBadge status={project.status} />
              </div>

              <h3 className="text-base font-bold text-slate-800 mb-2 group-hover:text-blue-600 transition-colors">
                {project.name}
              </h3>

              <div className="flex items-center text-xs text-slate-500 mb-6 gap-4">
                <span className="flex items-center gap-1"><Users size={14} /> {project.participantCount || 0}{t('dashboard.participantsCount')}</span>
                <span className="flex items-center gap-1"><Check size={14} /> {project.completedCount || 0}{t('dashboard.completedCount')}</span>
              </div>

              <div className="border-t border-slate-100 pt-4 flex justify-between items-center">
                <span className="text-[10px] text-slate-400">{t('dashboard.createdDate')}: {formatDate(project.createdAt)}</span>
                <span className="text-xs font-bold text-slate-400 group-hover:text-slate-800 flex items-center gap-1 transition-colors">
                  {t('dashboard.openDetail')} <ArrowRight size={14} />
                </span>
              </div>
            </div>
          ))}

          {/* Create New Placeholder Card */}
          <div
            onClick={() => setIsCreateProjectModalOpen(true)}
            className="border-2 border-dashed border-slate-200 rounded-xl p-6 flex flex-col items-center justify-center text-slate-400 hover:border-slate-400 hover:text-slate-600 hover:bg-slate-50 transition-all cursor-pointer min-h-[200px] group"
          >
            <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center mb-3 group-hover:bg-slate-200 transition-colors">
              <Plus size={20} />
            </div>
            <span className="text-sm font-bold">{t('dashboard.newProjectPlaceholder')}</span>
          </div>
        </div>
      )}
    </div>
  );

  // Project Detail View - Reference lines 720-1054
  const ProjectDetailView = () => {
    // Use designs from the useProject hook instead of mock data
    const projectDesigns = designs;

    const getTypeStyle = (type: string) => {
      switch (type) {
        case 'Discovery':
          return 'bg-sky-100 text-sky-700';
        case 'Validation':
          return 'bg-lime-100 text-lime-700';
        case 'Pricing':
          return 'bg-amber-100 text-amber-700';
        default:
          return 'bg-slate-100 text-slate-600';
      }
    };

    // Sub-view for full participant list per design
    if (activeDesignId) {
      const design = projectDesigns.find((d) => d.id === activeDesignId);
      // Filter participants by designId from the hook data
      const designParticipants = participants.filter((p) => p.designId === activeDesignId);

      return (
        <div className={`space-y-6 ${viewAnimationClass}`}>
          <div className="flex items-center gap-4 mb-6">
            <button
              onClick={() => setActiveDesignId(null)}
              className="text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1"
            >
              <ChevronRight size={14} className="rotate-180" /> {t('dashboard.back')}
            </button>
          </div>

          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-bold text-slate-800">{design?.name}</h2>
              <p className="text-sm text-slate-500">{t('dashboard.participantsList')}</p>
            </div>
            <button
              onClick={() => {
                setTargetDesignId(activeDesignId);
                setIsAddParticipantModalOpen(true);
              }}
              className="flex items-center gap-2 bg-slate-900 text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-slate-800"
            >
              <UserPlus size={16} /> {t('dashboard.addParticipant')}
            </button>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">{t('dashboard.participantName')}</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">{t('dashboard.date')}</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">{t('dashboard.duration')}</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">{t('dashboard.designStatus')}</th>
                  <th className="px-4 py-3 text-right text-xs font-bold text-slate-500 uppercase tracking-wider">{t('dashboard.action')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {designParticipants.map((p) => {
                  const participantName = p.name || p.candidate?.name || 'Unknown';
                  return (
                    <tr key={p.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-xs font-bold text-slate-600">
                            {participantName.charAt(0)}
                          </div>
                          <span className="font-medium text-sm text-slate-700">{participantName}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-500">{p.date || '-'}</td>
                      <td className="px-4 py-3 text-sm text-slate-500">{p.duration}</td>
                      <td className="px-4 py-3"><StatusBadge status={p.status} /></td>
                      <td className="px-4 py-3 text-right">
                        {p.status === 'completed' && (
                          <button
                            onClick={() => {
                              // Find result for this participant
                              const result = results.find(r => r.participantId === p.id);
                              if (result) {
                                setSelectedResultId(result.id);
                                setViewMode('result');
                              }
                            }}
                            className="text-xs text-blue-600 font-bold hover:underline"
                          >
                            {t('dashboard.seeResult')}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      );
    }

    return (
      <div className={`space-y-6 ${viewAnimationClass}`}>
        {/* Breadcrumb & Header */}
        <div className="flex flex-col gap-4">
          <button
            onClick={() => setViewMode('list')}
            className="text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1 w-fit"
          >
            <ChevronRight size={14} className="rotate-180" /> {t('dashboard.backToProjects')}
          </button>

          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
            <div>
              <div className="flex items-center gap-3 mb-1">
                <h2 className="text-xl font-bold text-slate-800">{selectedProject?.name}</h2>
                <StatusBadge status={selectedProject?.status || 'active'} />
              </div>
              <p className="text-xs text-slate-500">ID: PJ-{selectedProject?.id} • Category: {selectedProject?.category}</p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setIsExportModalOpen(true)}
                className="flex items-center gap-2 px-4 py-2 text-xs font-bold text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors shadow-sm"
              >
                <Download size={14} /> {t('dashboard.exportData')}
              </button>
              <button
                onClick={() => setViewMode('project_insight')}
                className="flex items-center gap-2 px-4 py-2 text-xs font-bold text-white bg-slate-900 border border-slate-900 rounded-lg hover:bg-slate-800 transition-colors shadow-sm"
              >
                <BarChart3 size={14} /> {t('dashboard.overallReport')}
              </button>
            </div>
          </div>
        </div>

        {/* Key Takeaways Section */}
        <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
          <h3 className="text-sm font-bold text-slate-800 mb-4 flex items-center gap-2">
            <Lightbulb size={16} className="text-amber-400 fill-amber-400" /> {t('dashboard.keyTakeaways')}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {insights?.keyTakeaways && insights.keyTakeaways.length > 0 ? (
              insights.keyTakeaways.map((item, idx) => (
                <div
                  key={idx}
                  className={`p-4 rounded-lg border flex flex-col gap-2 ${item.type === 'insight' ? 'bg-indigo-50/50 border-indigo-100' :
                    item.type === 'risk' ? 'bg-rose-50/50 border-rose-100' :
                      'bg-emerald-50/50 border-emerald-100'
                    }`}
                >
                  <div className="flex items-center gap-2">
                    {item.type === 'insight' && <Brain size={14} className="text-indigo-600" />}
                    {item.type === 'risk' && <AlertCircle size={14} className="text-rose-600" />}
                    {item.type === 'opportunity' && <TrendingUp size={14} className="text-emerald-600" />}
                    <span className={`text-xs font-bold uppercase tracking-wide ${item.type === 'insight' ? 'text-indigo-700' :
                      item.type === 'risk' ? 'text-rose-700' :
                        'text-emerald-700'
                      }`}>{item.type}</span>
                  </div>
                  <h4 className="text-xs font-bold text-slate-800">{item.title}</h4>
                  <p className="text-xs text-slate-600 leading-snug">{item.desc}</p>
                </div>
              ))
            ) : (
              // Placeholder cards when no insights
              [0, 1, 2].map((idx) => (
                <div
                  key={idx}
                  className="p-4 rounded-lg border flex flex-col gap-2 bg-slate-50/50 border-slate-200"
                >
                  <span className="text-slate-400 text-center py-4">-</span>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Interview Designs */}
        <div className="space-y-6">
          <div className="flex justify-between items-center px-1">
            <h3 className="text-base font-bold text-slate-800 flex items-center gap-2">
              <Layers size={18} className="text-slate-500" /> {t('dashboard.interviewDesigns')}
            </h3>
            <button
              onClick={() => setIsCreateDesignModalOpen(true)}
              className="flex items-center gap-1 text-xs font-bold text-blue-600 bg-blue-50 px-3 py-1.5 rounded hover:bg-blue-100 transition-colors"
            >
              <Plus size={14} /> {t('dashboard.newDesign')}
            </button>
          </div>

          {projectDesigns.map((design) => {
            // Filter participants from hook data for this specific design
            const designParticipants = participants.filter((p) => p.designId === design.id);

            return (
              <div
                key={design.id}
                className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden"
              >
                <div className="p-5 flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-100">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${getTypeStyle(design.type)}`}>
                        {design.type}
                      </span>
                      <h4 className="font-bold text-slate-800">{design.name}</h4>
                      <StatusBadge status={design.status} />
                    </div>
                    <p className="text-xs text-slate-500 mt-1">{design.description}</p>

                    <div className="flex items-center gap-4 mt-3 text-xs text-slate-500">
                      <span className="flex items-center gap-1"><MessageSquare size={12} /> {design.questions}{language === 'ja' ? '問' : ' Q'}</span>
                      <span className="flex items-center gap-1"><Clock size={12} /> {design.avgDuration}</span>
                    </div>

                    <div className="flex items-center gap-3 mt-3">
                      <span className="font-bold text-slate-700 whitespace-nowrap text-xs">{t('dashboard.progress')}: {design.completedCount}/{design.totalCount}</span>
                      <div className="flex-1 max-w-md bg-slate-100 h-2 rounded-full overflow-hidden">
                        <div
                          className="bg-blue-500 h-full rounded-full transition-all duration-500"
                          style={{ width: `${design.totalCount > 0 ? (design.completedCount / design.totalCount) * 100 : 0}%` }}
                        />
                      </div>
                      <span className="text-slate-400">|</span>
                      <span className="text-xs text-slate-500">{t('dashboard.questionCount', { count: design.questions })}</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      onClick={() => {
                        setSelectedDesign(design);
                        setViewMode('test_play');
                      }}
                      className="p-2 text-slate-400 hover:text-sky-600 hover:bg-sky-50 rounded-lg transition-colors"
                      title={t('dashboard.testPlay')}
                    >
                      <PlayCircle size={18} />
                    </button>
                    <button
                      onClick={() => {
                        setSelectedDesign(design);
                        setViewMode('template');
                      }}
                      className="p-2 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                      title={t('dashboard.editDesign')}
                    >
                      <Edit3 size={18} />
                    </button>
                    <button
                      onClick={() => {
                        setSelectedDesign(design);
                        setViewMode('insight');
                      }}
                      className="p-2 text-slate-400 hover:text-violet-600 hover:bg-violet-50 rounded-lg transition-colors"
                      title={t('dashboard.viewResults')}
                    >
                      <BarChart3 size={18} />
                    </button>
                    <button
                      onClick={() => {
                        setDesignToDelete(design);
                        setIsDeleteDesignModalOpen(true);
                      }}
                      className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title={t('dashboard.deleteDesign')}
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>

                {/* Participants - Table Layout */}
                {designParticipants.length > 0 ? (
                  <div className="bg-slate-50/50">
                    {/* Table Header with Add Participant */}
                    <div className="flex items-center justify-end px-4 pt-3 pb-1">
                      <button
                        onClick={() => {
                          setTargetDesignId(design.id);
                          setIsAddParticipantModalOpen(true);
                        }}
                        className="text-xs text-blue-600 hover:text-blue-800 font-bold flex items-center gap-1"
                      >
                        <UserPlus size={12} /> {t('dashboard.addParticipant')}
                      </button>
                    </div>
                    {/* Participants Table */}
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b border-slate-200">
                          <th className="py-2 px-4 text-left font-bold text-slate-500">{t('dashboard.recentParticipants')} ({designParticipants.length})</th>
                          <th className="py-2 px-4 text-left font-bold text-slate-500">{t('dashboard.designStatus')}</th>
                          <th className="py-2 px-4 text-right font-bold text-slate-500">{t('dashboard.action')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {designParticipants.map((p) => {
                          const pName = p.name || p.candidate?.name || 'Unknown';
                          return (
                            <tr key={p.id} className="border-b border-slate-100 last:border-b-0 hover:bg-white/50">
                              <td className="py-3 px-4">
                                <div className="flex items-center gap-3">
                                  <div className="w-7 h-7 rounded-full bg-slate-200 flex items-center justify-center text-xs font-bold text-slate-600">
                                    {pName.charAt(0)}
                                  </div>
                                  <span className="text-sm font-medium text-slate-700">{pName}</span>
                                </div>
                              </td>
                              <td className="py-3 px-4">
                                <StatusBadge status={p.status} />
                              </td>
                              <td className="py-3 px-4 text-right">
                                {p.status === 'completed' ? (
                                  <button
                                    onClick={() => {
                                      const result = results.find(r => r.participantId === p.id);
                                      if (result) {
                                        setSelectedResultId(result.id);
                                        setViewMode('result');
                                      } else {
                                        // TODO: Remove mock data after testing
                                        const pName = p.name || p.candidate?.name || 'Test Participant';
                                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                        const mockResult: any = {
                                          id: `mock-${p.id}`,
                                          projectId: selectedProjectId || '',
                                          participantId: p.id,
                                          designId: p.designId,
                                          candidateId: p.candidateId || '',
                                          analysis: {
                                            sentiment: 'positive',
                                            summary: 'テスト用のモックインタビュー結果',
                                            keyInsights: ['製品への関心', '価格感度', '使いやすさ重視'],
                                          },
                                          insights: [
                                            { type: 'positive', text: '製品機能に強い関心を示している' },
                                            { type: 'negative', text: '価格に対する懸念が言及された' },
                                            { type: 'opportunity', text: 'プレミアム機能のアップセルの可能性あり' },
                                          ],
                                          detailedReport: {
                                            background: `${pName}は30代の会社員で、日常的にデジタルサービスを利用しています。インタビューでは製品の使用経験について詳しく話してくれました。`,
                                            personaAnalysis: 'ユーザーは論理的思考を持ち、機能性を重視する傾向があります。新しい技術に対してオープンで、効率化に関心が高いです。',
                                            keyFindings: '1. 使いやすさが最も重要な要素\n2. 価格に対する価値の認識が購入決定に影響\n3. カスタマーサポートの質を重視',
                                            recommendation: '製品のオンボーディング体験を改善し、価値提案を明確にすることを推奨します。また、サポート体制の充実をアピールすることが効果的です。',
                                          },
                                          transcript: [
                                            { speaker: 'agent', text: 'こんにちは、本日はお時間をいただきありがとうございます。早速ですが、普段どのようなサービスをお使いですか？', timestamp: 0 },
                                            { speaker: 'user', text: 'よく使うのはオンラインショッピングとストリーミングサービスですね。便利なので毎日使っています。', timestamp: 15 },
                                            { speaker: 'agent', text: 'なるほど。それらのサービスを選ぶ際に、最も重視するポイントは何ですか？', timestamp: 45 },
                                            { speaker: 'user', text: 'やはり使いやすさですね。複雑な操作が必要なサービスは続かないです。', timestamp: 60 },
                                            { speaker: 'agent', text: '使いやすさは重要ですね。価格についてはいかがでしょうか？', timestamp: 90 },
                                            { speaker: 'user', text: '価格も大事ですが、価値に見合っていれば多少高くても払います。', timestamp: 105 },
                                          ],
                                          duration: 930,
                                          completedAt: { seconds: Date.now() / 1000, nanoseconds: 0 },
                                          candidateName: pName,
                                        };
                                        setSelectedResult(mockResult);
                                        setViewMode('result');
                                      }
                                    }}
                                    className="text-blue-600 font-bold hover:underline"
                                  >
                                    {t('dashboard.seeResult')}
                                  </button>
                                ) : p.status === 'pending' ? (
                                  <div className="flex items-center justify-end gap-1">
                                    {/* Open Interview Link */}
                                    <a
                                      href={`/${locale}/interview?participantId=${p.id}&designId=${p.designId}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-slate-400 hover:text-blue-600 p-1.5 rounded hover:bg-blue-50 transition-colors"
                                      title={t('dashboard.startInterview')}
                                    >
                                      <ExternalLink size={14} />
                                    </a>
                                    {/* Copy Link */}
                                    <button
                                      onClick={() => {
                                        const interviewUrl = `${window.location.origin}/${locale}/interview?participantId=${p.id}&designId=${p.designId}`;
                                        navigator.clipboard.writeText(interviewUrl);
                                        setCopiedLinkId(p.id);
                                        setTimeout(() => setCopiedLinkId(null), 2000);
                                      }}
                                      className={`p-1.5 rounded transition-colors ${copiedLinkId === p.id
                                        ? 'text-green-600 bg-green-50'
                                        : 'text-slate-400 hover:text-blue-600 hover:bg-blue-50'
                                        }`}
                                      title={copiedLinkId === p.id ? t('dashboard.copied') : t('dashboard.copyLink')}
                                    >
                                      {copiedLinkId === p.id ? <Check size={14} /> : <Copy size={14} />}
                                    </button>
                                    {/* Send Email */}
                                    <a
                                      href={`mailto:${p.candidate?.email || ''}?subject=${encodeURIComponent(t('dashboard.interviewInviteSubject'))}&body=${encodeURIComponent(
                                        t('dashboard.interviewInviteBody', {
                                          name: p.candidate?.name || p.name || '',
                                          link: `${window.location.origin}/${locale}/interview?participantId=${p.id}&designId=${p.designId}`,
                                        })
                                      )}`}
                                      className="text-slate-400 hover:text-blue-600 p-1.5 rounded hover:bg-blue-50 transition-colors"
                                      title={t('dashboard.sendEmail')}
                                    >
                                      <Mail size={14} />
                                    </a>
                                  </div>
                                ) : (
                                  <span className="text-slate-300">-</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="p-6 bg-slate-50/50 text-center">
                    <Users size={20} className="mx-auto text-slate-300 mb-2" />
                    <p className="text-xs text-slate-400">{t('dashboard.noParticipants')}</p>
                    <button
                      onClick={() => {
                        setTargetDesignId(design.id);
                        setIsAddParticipantModalOpen(true);
                      }}
                      className="mt-2 text-xs text-blue-600 font-bold hover:underline flex items-center gap-1 justify-center"
                    >
                      <UserPlus size={12} /> {t('dashboard.addParticipant')}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Template Editor View - Reference lines 1144-1429
  const TemplateEditorView = () => {
    // Debug: Log when this component renders and what the design state is
    console.log('TemplateEditorView rendering, selectedDesignIdState:', selectedDesignIdState, 'selectedDesign:', selectedDesign?.name);

    // Note: interviewerName is now managed globally via useUserSettings hook (Settings → Account)
    // genParams: Local state initialized from ref to persist across component recreations
    // The ref (genParamsRef) holds the current values even when this component is recreated
    const [genParams, setGenParamsLocal] = useState<GenParams>(() => genParamsRef.current);
    const [tone, setTone] = useState<'formal' | 'casual' | 'friendly'>('formal');
    const [savingDesign, setSavingDesign] = useState(false);

    // Wrapper that updates both local state and parent ref
    const setGenParams = useCallback((updater: GenParams | ((prev: GenParams) => GenParams)) => {
      setGenParamsLocal(prev => {
        const newValue = typeof updater === 'function' ? updater(prev) : updater;
        genParamsRef.current = newValue; // Keep ref in sync
        return newValue;
      });
    }, []);

    // Sync local state with ref when ref changes (e.g., when switching designs)
    useEffect(() => {
      // Check if ref has been updated by parent (design switch)
      const refValue = genParamsRef.current;
      if (
        refValue.target !== genParams.target ||
        refValue.topic !== genParams.topic ||
        refValue.goal !== genParams.goal ||
        refValue.usage !== genParams.usage
      ) {
        setGenParamsLocal(refValue);
      }
    }, [selectedDesign?.id]); // Re-sync when design changes

    const getQuestionTypeColor = (type: string) => {
      switch (type) {
        case 'Open':
          return 'bg-sky-100 text-sky-700 border-sky-200';
        case 'Value':
          return 'bg-violet-100 text-violet-700 border-violet-200';
        case 'Deep Dive':
          return 'bg-emerald-100 text-emerald-700 border-emerald-200';
        case 'Context':
          return 'bg-amber-100 text-amber-700 border-amber-200';
        case 'Ideal':
          return 'bg-rose-100 text-rose-700 border-rose-200';
        default:
          return 'bg-slate-100 text-slate-600 border-slate-200';
      }
    };

    // Check if required fields are filled
    const canGenerate = genParams.target.trim() && genParams.topic.trim() && genParams.goal.trim();

    // Use real AI question generation from hook
    const handleGenerateQuestions = async (e: React.MouseEvent) => {
      e.preventDefault();
      if (!canGenerate) {
        console.warn('Please fill in target, topic, and goal fields');
        return;
      }

      try {
        await generateQuestions({
          target: genParams.target.trim(),
          topic: genParams.topic.trim(),
          goal: genParams.goal.trim(),
          usage: genParams.usage.trim(),
          interviewerName,
          tone: tone as 'formal' | 'casual' | 'friendly',
          existingQuestions: templateQuestions.map(q => q.text),
        });

        // After generation completes, scroll to show generated questions
        setTimeout(() => {
          const scrollContainer = templateEditorScrollRef.current;
          if (scrollContainer) {
            // Update the saved position to bottom so it stays there
            templateEditorScrollPosRef.current = scrollContainer.scrollHeight;
            scrollContainer.scrollTop = scrollContainer.scrollHeight;
          }
        }, 150);
      } catch (error) {
        console.error('Failed to generate questions:', error);
      }
    };

    // Add generated question to template and save to design
    const addGeneratedQuestion = async (question: { text: string; type: string }) => {
      // Add to local state first for immediate feedback
      const newQuestion: TemplateQuestion = {
        id: templateQuestions.length + 1,
        text: question.text,
        type: question.type || 'Open',
        required: false,
      };
      setTemplateQuestions([...templateQuestions, newQuestion]);

      // Save to database if design is selected
      if (selectedDesignIdState) {
        try {
          await addQuestion({
            text: question.text,
            type: (question.type as 'Open' | 'Value' | 'DeepDive' | 'Context' | 'Ideal') || 'Open',
            required: false,
          });
        } catch (error) {
          console.error('Failed to save question:', error);
        }
      }
    };

    // Shared function to save design with auto-derived prompt configuration
    // Used by both "Save Design" and "Test Interview" buttons
    const saveDesignWithDerivedConfig = async (): Promise<boolean> => {
      if (!selectedDesignIdState) {
        console.warn('No design selected, cannot save');
        return false;
      }

      try {
        // Format questions as numbered list for the agent prompt
        const questionsText = templateQuestions.length > 0
          ? templateQuestions
            .sort((a, b) => a.id - b.id)
            .map((q, idx) => `${idx + 1}. ${q.text}`)
            .join('\n')
          : '';

        // Derive prompt variables if genParams are filled
        let derivedVariables: {
          primary_goal: string;
          secondary_goal: string;
          contextual_domain_notes: string;
          product_service: string;
        } | null = null;

        if (genParams.target && genParams.topic && genParams.goal) {
          try {
            console.log('Deriving prompt variables from genParams:', genParams);
            derivedVariables = await aiService.derivePromptVariables({
              target: genParams.target.trim(),
              topic: genParams.topic.trim(),
              goal: genParams.goal.trim(),
              usage: genParams.usage.trim(),
            });
            console.log('Derived variables:', derivedVariables);
          } catch (error) {
            console.error('Failed to derive prompt variables:', error);
            // Continue saving without derived variables
          }
        }

        // Build langfuseConfig with derived values
        const configToSave: LangfusePromptConfig = {
          promptName: 'decoa/agent_prompt',
          promptLabel: 'production',
          promptVariables: {
            name: interviewerName, // From user settings
            ...(derivedVariables || {}),
            additional_questions: questionsText || undefined,
          },
        };

        console.log('Saving design with config:', { designId: selectedDesignIdState, tone, genParams, configToSave });
        await updateDesign({
          tone,
          generationParams: genParams,
          langfuseConfig: configToSave,
        });

        // Refetch design to get updated langfuseConfig
        await refetchDesign();

        console.log('Design saved successfully with derived config');
        return true;
      } catch (error) {
        console.error('Failed to save design:', error);
        return false;
      }
    };

    // Save design settings (user clicks "Save Design" button)
    const handleSaveDesign = async () => {
      setSavingDesign(true);
      try {
        const success = await saveDesignWithDerivedConfig();
        if (success) {
          alert(language === 'ja' ? '保存しました' : 'Design saved successfully');
        } else {
          alert(language === 'ja' ? '保存に失敗しました' : 'Failed to save design');
        }
      } finally {
        setSavingDesign(false);
      }
    };

    return (
      <div className={`space-y-6 ${viewAnimationClass}`}>
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => setViewMode('detail')}
            className="text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1"
          >
            <ChevronRight size={14} className="rotate-180" /> {t('dashboard.back')}
          </button>
        </div>

        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold text-slate-800">{t('dashboard.interviewDesignEdit')}</h2>
            <p className="text-sm text-slate-500">{selectedDesign?.name}</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={async () => {
                setSavingDesign(true);
                try {
                  const success = await saveDesignWithDerivedConfig();
                  if (success) {
                    setViewMode('test_play');
                  } else {
                    alert(language === 'ja' ? 'デザインの保存に失敗しました' : 'Failed to save design before test');
                  }
                } finally {
                  setSavingDesign(false);
                }
              }}
              disabled={savingDesign}
              className="flex items-center gap-2 bg-white text-slate-700 px-3 py-2 rounded-lg text-xs font-bold border border-slate-200 hover:bg-slate-50 disabled:opacity-50"
            >
              {savingDesign ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <PlayCircle size={14} />
              )}
              {t('dashboard.testPlay')}
            </button>
            <button
              onClick={() => {
                console.log('Save button clicked!');
                handleSaveDesign();
              }}
              disabled={savingDesign}
              className="flex items-center gap-2 bg-slate-900 text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-slate-800 disabled:opacity-50"
            >
              {savingDesign && <Loader2 size={14} className="animate-spin" />}
              {t('dashboard.saveTemplate')}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-[calc(100vh-200px)]">
          {/* Left Column - Basic Settings & Generation Parameters */}
          <div ref={templateEditorScrollRef} onScroll={handleTemplateEditorScroll} className="space-y-6 overflow-y-auto">
            {/* Basic Settings */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
              <h3 className="text-sm font-bold text-slate-800 mb-4">{t('dashboard.basicSettings')}</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">{t('dashboard.tone')}</label>
                  <select
                    value={tone}
                    onChange={(e) => setTone(e.target.value as 'formal' | 'casual' | 'friendly')}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="formal">{language === 'ja' ? 'フォーマル' : 'Formal'}</option>
                    <option value="friendly">{language === 'ja' ? 'フレンドリー' : 'Friendly'}</option>
                    <option value="casual">{language === 'ja' ? 'カジュアル' : 'Casual'}</option>
                  </select>
                </div>
              </div>
            </div>

            {/* AI Generation Parameters */}
            <div className="bg-gradient-to-br from-violet-50 to-blue-50 rounded-xl border border-violet-100 p-5">
              <div className="flex items-center gap-2 mb-3">
                <Wand2 size={18} className="text-violet-600" />
                <h3 className="text-sm font-bold text-slate-800">{t('dashboard.genParams')}</h3>
              </div>
              <p className="text-xs text-slate-500 mb-4">{t('dashboard.aiWizardSubtitle')}</p>

              {/* Use memoized component to prevent focus loss on re-render */}
              <GenParamsForm genParams={genParams} setGenParams={setGenParams} t={t} />

              <button
                type="button"
                onClick={handleGenerateQuestions}
                disabled={aiLoading || !canGenerate}
                className="w-full mt-4 flex items-center justify-center gap-2 bg-gradient-to-r from-violet-600 to-blue-600 text-white px-4 py-3 rounded-lg text-sm font-bold hover:from-violet-700 hover:to-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {aiLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                    {t('dashboard.generating')}
                  </>
                ) : (
                  <>
                    <Sparkles size={16} /> {t('dashboard.generateBtn')}
                  </>
                )}
              </button>

              {generatedQuestionsAI.length > 0 && (
                <div className="mt-4 space-y-2">
                  {generatedQuestionsAI.map((question, index) => (
                    <div
                      key={index}
                      className="p-3 bg-white border border-violet-200 rounded-lg animate-in slide-in-from-bottom-2 duration-300"
                    >
                      <p className="text-sm text-slate-700 mb-2">{question.text}</p>
                      <button
                        onClick={() => addGeneratedQuestion(question)}
                        className="text-xs text-violet-600 hover:text-violet-800 flex items-center gap-1 font-bold"
                      >
                        <Plus size={12} /> {t('dashboard.addToTemplate')}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Questions List */}
          <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 shadow-sm flex flex-col overflow-hidden">
            <div className="p-5 border-b border-slate-100 flex justify-between items-center">
              <h3 className="text-sm font-bold text-slate-800">{t('dashboard.questionList')}</h3>
              <span className="text-xs text-slate-400">{t('dashboard.questionCount', { count: templateQuestions.length })}</span>
            </div>

            {templateQuestions.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center p-10">
                <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mb-4">
                  <MessageSquare size={28} className="text-slate-300" />
                </div>
                <h4 className="font-medium text-slate-600 mb-1">{t('dashboard.emptyQuestions')}</h4>
                <p className="text-xs text-slate-400">{t('dashboard.emptyQuestionsSub')}</p>
              </div>
            ) : (
              <div className="flex-1 overflow-y-auto divide-y divide-slate-100">
                {templateQuestions.map((question, index) => (
                  <div
                    key={question.id}
                    className="p-4 hover:bg-slate-50 transition-colors group"
                  >
                    <div className="flex items-start gap-3">
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity cursor-grab pt-1">
                        <GripVertical size={16} className="text-slate-300" />
                      </div>
                      <div className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center text-xs font-bold text-slate-500 shrink-0">
                        {index + 1}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1.5">
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded border ${getQuestionTypeColor(question.type)}`}>
                            {question.type}
                          </span>
                        </div>
                        <p className="text-sm text-slate-700">{question.text}</p>
                      </div>
                      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded">
                          <Settings size={14} />
                        </button>
                        <button className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="p-4 border-t border-dashed border-slate-200 bg-slate-50/50">
              <button className="w-full py-2.5 border-2 border-dashed border-slate-200 rounded-lg text-slate-400 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50/50 transition-all text-xs font-bold flex items-center justify-center gap-2">
                <Plus size={14} /> {t('dashboard.manualAdd')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Test Play View (Interview Screen) - Reference lines 1478-1571
  // Inner component that uses the connection context
  // Motion-wrapped SessionView for exit animation (matches home page behavior)
  const MotionSessionView = motion.create(SessionView);
  const SESSION_VIEW_MOTION_PROPS = {
    variants: {
      visible: { opacity: 1 },
      hidden: { opacity: 0 },
    },
    initial: 'hidden',
    animate: 'visible',
    exit: 'hidden',
    transition: { duration: 0.3, ease: 'linear' },
  };

  const InterviewScreenContent = () => {
    const { isConnectionActive, connect, onDisconnectTransitionComplete } = useConnection();
    const appConfig = getAppConfig();
    const hasConnectedRef = useRef(false);

    // Auto-connect when mounted (only once)
    useEffect(() => {
      if (!hasConnectedRef.current && !isConnectionActive) {
        hasConnectedRef.current = true;
        connect();
      }
    }, [connect, isConnectionActive]);

    // Handle animation complete - this is when we actually end the session
    // This matches the home page behavior where cleanup happens after exit animation
    const handleAnimationComplete = useCallback(
      (definition: AnimationDefinition) => {
        if (definition === 'hidden' && hasConnectedRef.current) {
          console.log('[InterviewScreenContent] Exit animation complete, ending session...');
          onDisconnectTransitionComplete();
          // Delay view mode change to allow session/microphone cleanup to fully complete
          // The ConnectionProvider needs time to cleanup before unmounting
          setTimeout(() => {
            console.log('[InterviewScreenContent] Session cleanup complete, navigating to template view...');
            setViewMode('template');
          }, 500);
        }
      },
      [onDisconnectTransitionComplete]
    );

    return (
      <div className="fixed inset-0 z-[100] flex flex-col dark bg-slate-900">
        {/* Design info badge */}
        {selectedDesign && (
          <div className="absolute top-4 left-4 z-50">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 text-white/80 text-xs font-medium">
              <PlayCircle size={14} />
              {language === 'ja' ? 'テスト中:' : 'Testing:'} {selectedDesign.name}
            </span>
          </div>
        )}

        {/* LiveKit Session View with AnimatePresence (matches home page) */}
        <AnimatePresence mode="wait">
          {isConnectionActive ? (
            <MotionSessionView
              key="session-view"
              {...SESSION_VIEW_MOTION_PROPS}
              appConfig={appConfig}
              className="text-white"
              onAnimationComplete={handleAnimationComplete}
            />
          ) : !hasConnectedRef.current ? (
            <motion.div
              key="connecting"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex-1 flex items-center justify-center"
            >
              <div className="text-center text-white">
                <Loader2 size={48} className="animate-spin mx-auto mb-4 opacity-50" />
                <p className="text-white/70">{language === 'ja' ? '接続中...' : 'Connecting...'}</p>
              </div>
            </motion.div>
          ) : null}
        </AnimatePresence>

        <StartAudio label="Start Audio" />
        <RoomAudioRenderer />
        <Toaster />
      </div>
    );
  };

  // Wrapper that provides the connection context with design-specific config
  const InterviewScreenView = () => {
    const appConfig = getAppConfig();

    // If no design selected, show error
    if (!selectedDesign) {
      return (
        <div className="fixed inset-0 bg-slate-900/90 z-[100] flex items-center justify-center">
          <div className="text-center text-white">
            <AlertCircle size={48} className="mx-auto mb-4 text-amber-400" />
            <p>{language === 'ja' ? 'デザインを選択してください' : 'Please select a design'}</p>
            <button
              onClick={() => setViewMode('list')}
              className="mt-4 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
            >
              {language === 'ja' ? '戻る' : 'Go Back'}
            </button>
          </div>
        </div>
      );
    }

    return (
      <UserNameProvider>
        <ConnectionProvider
          appConfig={appConfig}
          designId={selectedDesign.id}
          // designLangfuseConfig={selectedDesign.langfuseConfig} // Removed - using prompt_config_id interview mode
        >
          <InterviewScreenContent />
        </ConnectionProvider>
      </UserNameProvider>
    );
  };

  // Project Insight View - Reference lines 1573-1773
  const ProjectInsightView = () => {
    // Note: insightTab state is managed at parent level to persist across re-renders
    const [chatInput, setChatInput] = useState('');
    const messagesEndRef = useRef<HTMLDivElement>(null);

    // Welcome message - always shown as first message in chat
    const welcomeMessage = language === 'ja'
      ? 'インタビューデータの分析結果についてご質問があればお答えします。特定のトピックや傾向について詳しくお知りになりたい場合は、お気軽にお尋ねください。'
      : 'I can answer questions about the interview data analysis. Feel free to ask about specific topics or trends you would like to know more about.';

    useEffect(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [chatMessages]);

    const handleSendMessage = async () => {
      if (!chatInput.trim() || chatLoading) return;
      const message = chatInput;
      setChatInput('');

      try {
        await sendMessage(message);
      } catch (error) {
        console.error('Failed to send message:', error);
      }
    };

    return (
      <div className={`space-y-6 ${viewAnimationClass}`}>
        <div className="flex flex-col gap-4 mb-6">
          <button
            onClick={() => setViewMode('detail')}
            className="text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1 w-fit"
          >
            <ChevronRight size={14} className="rotate-180" /> {t('dashboard.backToProjects')}
          </button>

          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
            <div>
              <h2 className="text-xl font-bold text-slate-800 mb-2">{t('dashboard.overallReport')}</h2>
              <div className="flex items-center gap-1 p-1 bg-slate-100 rounded-lg w-fit">
                <button
                  onClick={() => setInsightTab('report')}
                  className={`px-4 py-1.5 text-xs font-bold rounded-md transition-all ${insightTab === 'report' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                    }`}
                >
                  {t('dashboard.reportTab')}
                </button>
                <button
                  onClick={() => setInsightTab('chat')}
                  className={`px-4 py-1.5 text-xs font-bold rounded-md transition-all flex items-center gap-1.5 ${insightTab === 'chat' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                    }`}
                >
                  <Bot size={14} /> {t('dashboard.chatTab')}
                </button>
              </div>
            </div>
            {insightTab === 'report' && (
              <div className="flex gap-2">
                <button
                  onClick={async () => {
                    try {
                      await generateInsights();
                    } catch (error) {
                      console.error('Failed to generate insights:', error);
                    }
                  }}
                  disabled={insightsGenerating}
                  className="px-3 py-1.5 text-xs font-bold text-white bg-gradient-to-r from-violet-600 to-blue-600 rounded hover:from-violet-700 hover:to-blue-700 flex items-center gap-2 disabled:opacity-50 transition-all"
                >
                  {insightsGenerating ? (
                    <>
                      <Loader2 size={14} className="animate-spin" />
                      {t('dashboard.generating')}
                    </>
                  ) : (
                    <>
                      <Sparkles size={14} />
                      {language === 'ja' ? 'インサイト生成' : 'Generate Insights'}
                    </>
                  )}
                </button>
                <button className="px-3 py-1.5 text-xs font-bold text-slate-600 border border-slate-200 rounded hover:bg-slate-50 flex items-center gap-2">
                  <FileText size={14} /> {t('dashboard.savePdf')}
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Left: Summary Cards */}
          <div className="space-y-6">
            <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">{t('dashboard.executiveSummary')}</h3>
              <p className="text-sm leading-relaxed text-slate-800 font-medium">
                {insights?.executiveSummary || (language === 'ja' ? 'インサイトを生成してください' : 'Please generate insights')}
              </p>
            </div>

            <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">{t('dashboard.keyThemes')}</h3>
              <div className="flex flex-wrap gap-2">
                {insights?.keyThemes && insights.keyThemes.length > 0 ? (
                  insights.keyThemes.map((theme, i) => (
                    <span
                      key={i}
                      className={`text-[10px] px-2 py-1 rounded border font-medium ${theme.type === 'positive' ? 'bg-emerald-50 border-emerald-100 text-emerald-700' :
                        theme.type === 'negative' ? 'bg-rose-50 border-rose-100 text-rose-700' :
                          'bg-slate-50 border-slate-200 text-slate-600'
                        }`}
                    >
                      {theme.name} ({theme.count})
                    </span>
                  ))
                ) : (
                  <span className="text-xs text-slate-400">{language === 'ja' ? 'データなし' : 'No data'}</span>
                )}
              </div>
            </div>
          </div>

          {/* Right: Dynamic Content */}
          <div className="lg:col-span-3 bg-white rounded-xl border border-slate-200 shadow-sm min-h-[600px] overflow-hidden flex flex-col">
            {insightTab === 'report' ? (
              <div className="p-8 font-serif md:font-sans">
                <div className="mb-8 border-b border-slate-100 pb-4">
                  <h2 className="text-2xl font-bold text-slate-900 mb-2">{selectedProject?.name} 分析報告書</h2>
                  <p className="text-sm text-slate-500">{language === 'ja' ? '生成日' : 'Generated'}: {new Date().toLocaleDateString(language === 'ja' ? 'ja-JP' : 'en-US')} | {language === 'ja' ? '作成者' : 'By'}: HearO AI Analytics</p>
                </div>

                <div className="space-y-8 text-slate-800 leading-relaxed">
                  {insights?.detailedAnalysis && insights.detailedAnalysis.length > 0 ? (
                    <>
                      {insights.detailedAnalysis.map((section, idx) => (
                        <section key={idx}>
                          <h3 className="text-lg font-bold text-slate-900 mb-3">{section.title}</h3>
                          <p className="text-sm text-slate-700 whitespace-pre-wrap">{section.content}</p>
                        </section>
                      ))}

                      <div className="p-6 bg-blue-50/50 rounded-lg border border-blue-100 mt-8">
                        <h4 className="text-sm font-bold text-blue-800 mb-2 flex items-center gap-2">
                          <Sparkles size={16} /> {language === 'ja' ? 'AI推奨アクション' : 'AI Recommended Actions'}
                        </h4>
                        {insights?.recommendations && insights.recommendations.length > 0 ? (
                          <ul className="list-disc list-inside text-sm text-slate-700 space-y-2">
                            {insights.recommendations.map((rec, idx) => (
                              <li key={idx}>{rec}</li>
                            ))}
                          </ul>
                        ) : (
                          <p className="text-sm text-slate-400">{language === 'ja' ? '推奨アクションなし' : 'No recommendations'}</p>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-12 text-slate-400">
                      <Sparkles size={32} className="mx-auto mb-4 opacity-50" />
                      <p className="text-sm">{language === 'ja' ? '「インサイト生成」ボタンをクリックして分析を開始してください' : 'Click "Generate Insights" to start analysis'}</p>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex flex-col h-full bg-slate-50/30">
                <div className="p-4 border-b border-slate-100 bg-white flex justify-between items-center">
                  <div>
                    <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                      <Sparkles size={14} className="text-blue-500" /> {t('dashboard.aiAnalyst')}
                    </h3>
                    <p className="text-[10px] text-slate-500">{t('dashboard.aiAnalystSub')}</p>
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto p-6 space-y-6">
                  {/* Welcome message - always shown as first message */}
                  <div className="flex gap-3 flex-row">
                    <div className="w-8 h-8 rounded-full bg-slate-900 text-white flex items-center justify-center text-[10px] font-bold shrink-0 mt-1">AI</div>
                    <div className="max-w-[80%] rounded-2xl p-4 text-sm leading-relaxed shadow-sm bg-white text-slate-700 rounded-tl-none border border-slate-200">
                      {welcomeMessage}
                    </div>
                  </div>
                  {chatMessages.map((msg, index) => (
                    <div key={index} className={`flex gap-3 ${msg.role === 'assistant' ? 'flex-row' : 'flex-row-reverse'}`}>
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 mt-1 ${msg.role === 'assistant' ? 'bg-slate-900 text-white' : 'bg-blue-100 text-blue-700'
                        }`}>
                        {msg.role === 'assistant' ? 'AI' : 'U'}
                      </div>
                      <div className={`max-w-[80%] rounded-2xl p-4 text-sm leading-relaxed shadow-sm ${msg.role === 'assistant' ? 'bg-white text-slate-700 rounded-tl-none border border-slate-200' : 'bg-blue-600 text-white rounded-tr-none'
                        }`}>
                        {msg.content}
                      </div>
                    </div>
                  ))}
                  {chatLoading && (
                    <div className="flex gap-3">
                      <div className="w-8 h-8 rounded-full bg-slate-900 text-white flex items-center justify-center text-[10px] font-bold shrink-0">AI</div>
                      <div className="bg-white border border-slate-200 rounded-2xl rounded-tl-none p-4 flex gap-1 items-center shadow-sm">
                        <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" />
                        <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '75ms' }} />
                        <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>

                <div className="p-4 bg-white border-t border-slate-200">
                  <div className="flex gap-2 relative">
                    <input
                      type="text"
                      value={chatInput}
                      onChange={(e) => setChatInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                      placeholder={t('dashboard.aiChatPlaceholder')}
                      className="flex-1 bg-slate-50 border border-slate-200 text-sm rounded-lg pl-4 pr-12 py-3 focus:outline-none focus:border-blue-500 focus:bg-white transition-all"
                    />
                    <button
                      onClick={handleSendMessage}
                      disabled={!chatInput.trim() || chatLoading}
                      className="absolute right-2 top-2 p-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 transition-colors"
                    >
                      <Send size={16} />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Result Detail View - Reference lines 1775-1879
  const ResultDetailView = () => {
    // Download transcript log handler
    const handleDownloadLog = () => {
      if (!selectedResult?.transcript || selectedResult.transcript.length === 0) {
        alert(isJapanese ? 'ダウンロードするトランスクリプトがありません' : 'No transcript available to download');
        return;
      }

      const candidateName = (selectedResult as InterviewResult & { candidateName?: string }).candidateName || 'participant';
      const logContent = selectedResult.transcript
        .map(t => `[${t.speaker === 'agent' ? 'AI' : 'User'}]: ${t.text}`)
        .join('\n\n');

      const header = `Interview Transcript - ${candidateName}\nDate: ${new Date().toLocaleString()}\nDuration: ${selectedResult.duration || 'N/A'}\n${'='.repeat(50)}\n\n`;

      const blob = new Blob([header + logContent], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `interview-log-${candidateName.replace(/\s+/g, '_')}-${new Date().toISOString().split('T')[0]}.txt`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    };

    // Save as PDF handler (uses browser print)
    const handleSavePdf = () => {
      window.print();
    };

    // Handle loading state
    if (resultLoading) {
      return (
        <div className="flex items-center justify-center h-[50vh]">
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
            <p className="text-sm text-slate-500">{t('dashboard.loading')}</p>
          </div>
        </div>
      );
    }

    // Handle no result selected
    if (!selectedResult) {
      return (
        <div className="flex flex-col items-center justify-center h-[50vh] gap-4">
          <p className="text-sm text-slate-500">{t('dashboard.noData')}</p>
          <button
            onClick={() => setViewMode('detail')}
            className="px-4 py-2 text-sm font-bold text-slate-600 border border-slate-200 rounded hover:bg-slate-50"
          >
            {t('dashboard.back')}
          </button>
        </div>
      );
    }

    return (
      <div className={`space-y-6 ${viewAnimationClass}`}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => {
                setViewMode('detail');
                setSelectedResultId(null);
              }}
              className="p-2 hover:bg-slate-100 rounded-lg text-slate-500"
            >
              <ChevronRight size={20} className="rotate-180" />
            </button>
            <div>
              <h2 className="text-lg font-bold text-slate-800">{t('dashboard.interviewResultDetail')}</h2>
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <span className="font-bold text-slate-700">{(selectedResult as InterviewResult & { candidateName?: string }).candidateName || 'Participant'}</span>
                <span className="text-slate-400">|</span>
                <span>{t('dashboard.designStatus')}: </span>
                <StatusBadge status="completed" />
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleDownloadLog}
              className="px-3 py-1.5 text-xs font-bold text-slate-600 border border-slate-200 rounded hover:bg-slate-50 transition-colors"
            >
              {t('dashboard.downloadLog')}
            </button>
            <button
              onClick={handleSavePdf}
              className="px-3 py-1.5 text-xs font-bold text-white bg-slate-900 rounded hover:bg-slate-800 flex items-center gap-2 transition-colors"
            >
              <FileText size={14} /> {t('dashboard.savePdf')}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 h-[calc(100vh-200px)]">
          {/* Left: Detailed Report */}
          <div className="lg:col-span-8 bg-white rounded-xl border border-slate-200 shadow-sm flex flex-col h-full overflow-hidden">
            <div className="p-6 border-b border-slate-100 bg-white sticky top-0 z-10">
              <h3 className="text-base font-bold text-slate-800 flex items-center gap-2">
                <FileText size={18} className="text-blue-600" /> {t('dashboard.individualReport')}
              </h3>
            </div>
            <div className="flex-1 overflow-y-auto p-8 space-y-6 bg-white">
              {/* Show analyzing state */}
              {analyzing ? (
                <div className="flex flex-col items-center justify-center h-full gap-4">
                  <div className="relative">
                    <div className="w-16 h-16 border-4 border-blue-200 rounded-full"></div>
                    <div className="absolute top-0 left-0 w-16 h-16 border-4 border-blue-600 rounded-full border-t-transparent animate-spin"></div>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-bold text-slate-800">
                      {isJapanese ? 'インタビューを分析中...' : 'Analyzing the interview...'}
                    </p>
                    <p className="text-sm text-slate-500 mt-1">
                      {isJapanese ? 'AIがインタビュー内容を詳細に分析しています' : 'AI is analyzing the interview content in detail'}
                    </p>
                  </div>
                </div>
              ) : selectedResult.detailedReport?.score !== undefined ? (
                /* NEW FORMAT - Langfuse analysis_prompt output */
                <>
                  {/* Score & Summary Header */}
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-lg border border-blue-100">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-bold text-slate-700">スコア</span>
                      <span className="text-2xl font-bold text-blue-600">
                        {selectedResult.detailedReport?.score ?? '-'}/100
                      </span>
                    </div>
                    {selectedResult.detailedReport?.summary && (
                      <p className="text-sm text-slate-600">{selectedResult.detailedReport.summary}</p>
                    )}
                  </div>

                  {/* Participant Profile - only show if at least one field has a value */}
                  {(selectedResult.detailedReport?.participant_profile?.name ||
                    selectedResult.detailedReport?.participant_profile?.occupation ||
                    selectedResult.detailedReport?.participant_profile?.plan ||
                    selectedResult.detailedReport?.participant_profile?.start_date ||
                    selectedResult.detailedReport?.participant_profile?.referral_source) && (
                      <section>
                        <h4 className="text-sm font-bold text-slate-900 mb-2 border-l-4 border-blue-500 pl-3">
                          参加者プロフィール
                        </h4>
                        <div className="grid grid-cols-2 gap-3 text-sm bg-slate-50 p-4 rounded-lg">
                          {selectedResult.detailedReport?.participant_profile?.name && (
                            <div><span className="text-slate-500">名前:</span> {selectedResult.detailedReport.participant_profile.name}</div>
                          )}
                          {selectedResult.detailedReport?.participant_profile?.occupation && (
                            <div><span className="text-slate-500">職業:</span> {selectedResult.detailedReport.participant_profile.occupation}</div>
                          )}
                          {selectedResult.detailedReport?.participant_profile?.plan && (
                            <div><span className="text-slate-500">プラン:</span> {selectedResult.detailedReport.participant_profile.plan}</div>
                          )}
                          {selectedResult.detailedReport?.participant_profile?.start_date && (
                            <div><span className="text-slate-500">開始日:</span> {selectedResult.detailedReport.participant_profile.start_date}</div>
                          )}
                          {selectedResult.detailedReport?.participant_profile?.referral_source && (
                            <div className="col-span-2"><span className="text-slate-500">紹介元:</span> {selectedResult.detailedReport.participant_profile.referral_source}</div>
                          )}
                        </div>
                      </section>
                    )}

                  {/* Life Context & Goals */}
                  <section>
                    <h4 className="text-sm font-bold text-slate-900 mb-2 border-l-4 border-purple-500 pl-3">
                      背景・目標
                    </h4>
                    <div className="space-y-3">
                      {selectedResult.detailedReport?.inferred_life_context && (
                        <div>
                          <span className="text-xs text-slate-500 block mb-1">生活背景</span>
                          <p className="text-sm text-slate-700 leading-relaxed">{selectedResult.detailedReport.inferred_life_context}</p>
                        </div>
                      )}
                      {selectedResult.detailedReport?.goals_and_motivations && (
                        <div>
                          <span className="text-xs text-slate-500 block mb-1">目標・動機</span>
                          <p className="text-sm text-slate-700 leading-relaxed">{selectedResult.detailedReport.goals_and_motivations}</p>
                        </div>
                      )}
                    </div>
                  </section>

                  {/* Usage & Triggers */}
                  <section>
                    <h4 className="text-sm font-bold text-slate-900 mb-2 border-l-4 border-emerald-500 pl-3">
                      利用状況
                    </h4>
                    <div className="space-y-3">
                      {selectedResult.detailedReport?.triggers && (
                        <div>
                          <span className="text-xs text-slate-500 block mb-1">きっかけ</span>
                          <p className="text-sm text-slate-700 leading-relaxed">{selectedResult.detailedReport.triggers}</p>
                        </div>
                      )}
                      {selectedResult.detailedReport?.usage_patterns && (
                        <div>
                          <span className="text-xs text-slate-500 block mb-1">利用パターン</span>
                          <p className="text-sm text-slate-700 leading-relaxed">{selectedResult.detailedReport.usage_patterns}</p>
                        </div>
                      )}
                      {selectedResult.detailedReport?.decision_factors && (
                        <div>
                          <span className="text-xs text-slate-500 block mb-1">意思決定要因</span>
                          <p className="text-sm text-slate-700 leading-relaxed">{selectedResult.detailedReport.decision_factors}</p>
                        </div>
                      )}
                    </div>
                  </section>

                  {/* Expectations & Pain Points */}
                  <section>
                    <h4 className="text-sm font-bold text-slate-900 mb-2 border-l-4 border-amber-500 pl-3">
                      期待と課題
                    </h4>
                    <div className="space-y-3">
                      {selectedResult.detailedReport?.expectation_vs_reality && (
                        <div>
                          <span className="text-xs text-slate-500 block mb-1">期待 vs 現実</span>
                          <p className="text-sm text-slate-700 leading-relaxed">{selectedResult.detailedReport.expectation_vs_reality}</p>
                        </div>
                      )}
                      {selectedResult.detailedReport?.pain_points && (
                        <div>
                          <span className="text-xs text-slate-500 block mb-1">課題・不満</span>
                          <p className="text-sm text-slate-700 leading-relaxed">{selectedResult.detailedReport.pain_points}</p>
                        </div>
                      )}
                    </div>
                  </section>

                  {/* Insights & Recommendations */}
                  <section>
                    <h4 className="text-sm font-bold text-slate-900 mb-2 border-l-4 border-rose-500 pl-3">
                      インサイト
                    </h4>
                    <div className="space-y-3">
                      {selectedResult.detailedReport?.core_insight && (
                        <div className="bg-rose-50 p-3 rounded-lg border border-rose-100">
                          <span className="text-xs text-rose-600 font-bold block mb-1">コアインサイト</span>
                          <p className="text-sm text-slate-700 leading-relaxed">{selectedResult.detailedReport.core_insight}</p>
                        </div>
                      )}
                      {selectedResult.detailedReport?.improvement_ideas && (
                        <div>
                          <span className="text-xs text-slate-500 block mb-1">改善アイデア</span>
                          <p className="text-sm text-slate-700 leading-relaxed">{selectedResult.detailedReport.improvement_ideas}</p>
                        </div>
                      )}
                      {selectedResult.detailedReport?.retention_drivers && (
                        <div>
                          <span className="text-xs text-slate-500 block mb-1">継続要因</span>
                          <p className="text-sm text-slate-700 leading-relaxed">{selectedResult.detailedReport.retention_drivers}</p>
                        </div>
                      )}
                      {selectedResult.detailedReport?.hypothesis_testing_signals && (
                        <div>
                          <span className="text-xs text-slate-500 block mb-1">仮説検証シグナル</span>
                          <p className="text-sm text-slate-700 leading-relaxed">{selectedResult.detailedReport.hypothesis_testing_signals}</p>
                        </div>
                      )}
                    </div>
                  </section>

                  {/* Quotes */}
                  {selectedResult.detailedReport?.quotes && selectedResult.detailedReport.quotes.length > 0 && (
                    <section>
                      <h4 className="text-sm font-bold text-slate-900 mb-2 border-l-4 border-cyan-500 pl-3">
                        印象的な発言
                      </h4>
                      <div className="space-y-2">
                        {selectedResult.detailedReport.quotes.map((quote, i) => (
                          <blockquote key={i} className="text-sm text-slate-600 italic border-l-2 border-cyan-200 pl-3 py-1">
                            "{quote}"
                          </blockquote>
                        ))}
                      </div>
                    </section>
                  )}
                </>
              ) : (
                /* OLD FORMAT - Legacy detailedReport structure */
                <>
                  {(selectedResult.detailedReport as unknown as { background?: string })?.background && (
                    <section>
                      <h4 className="text-sm font-bold text-slate-900 mb-2 border-l-4 border-blue-500 pl-3">{t('dashboard.backgroundContext')}</h4>
                      <p className="text-sm leading-7 text-slate-700">{(selectedResult.detailedReport as unknown as { background?: string }).background}</p>
                    </section>
                  )}

                  {(selectedResult.detailedReport as unknown as { personaAnalysis?: string })?.personaAnalysis && (
                    <section>
                      <h4 className="text-sm font-bold text-slate-900 mb-2 border-l-4 border-purple-500 pl-3">{t('dashboard.personaAnalysis')}</h4>
                      <div className="bg-purple-50/50 p-4 rounded-lg border border-purple-100 mb-3">
                        <p className="text-sm leading-7 text-slate-700">{(selectedResult.detailedReport as unknown as { personaAnalysis?: string }).personaAnalysis}</p>
                      </div>
                    </section>
                  )}

                  {(selectedResult.detailedReport as unknown as { keyFindings?: string })?.keyFindings && (
                    <section>
                      <h4 className="text-sm font-bold text-slate-900 mb-2 border-l-4 border-emerald-500 pl-3">{t('dashboard.keyFindings')}</h4>
                      <p className="text-sm leading-7 text-slate-700">{(selectedResult.detailedReport as unknown as { keyFindings?: string }).keyFindings}</p>
                    </section>
                  )}

                  {(selectedResult.detailedReport as unknown as { recommendation?: string })?.recommendation && (
                    <section>
                      <h4 className="text-sm font-bold text-slate-900 mb-2 border-l-4 border-amber-500 pl-3">{t('dashboard.recommendation')}</h4>
                      <p className="text-sm leading-7 text-slate-700">{(selectedResult.detailedReport as unknown as { recommendation?: string }).recommendation}</p>
                    </section>
                  )}

                  {/* Note about old format - auto-analysis should have triggered */}
                  <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 mt-4">
                    <p className="text-sm text-slate-600">
                      ℹ️ {isJapanese ? 'このレポートは以前のフォーマットで保存されています。' : 'This report was saved in the previous format.'}
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Right: Transcript & Key Points */}
          <div className="lg:col-span-4 flex flex-col gap-6 h-full overflow-hidden">
            <div className="flex-1 bg-white rounded-xl border border-slate-200 shadow-sm flex flex-col overflow-hidden">
              <div className="p-4 border-b border-slate-100 bg-slate-50 flex justify-between items-center shrink-0">
                <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                  <MessageSquare size={16} /> {t('dashboard.transcript')}
                </h3>
                <span className="text-[10px] text-slate-500">{selectedResult.duration || '-'}</span>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-white">
                {(selectedResult.transcript || []).map((msg, i) => (
                  <div key={i} className={`flex flex-col gap-1 ${msg.speaker === 'agent' ? 'items-start' : 'items-end'}`}>
                    <div className="text-[10px] text-slate-400 font-bold px-1">{msg.speaker === 'agent' ? 'AI' : 'User'}</div>
                    <div className={`max-w-[90%] rounded-lg p-3 text-xs leading-relaxed border ${msg.speaker === 'agent' ? 'bg-slate-50 text-slate-700 border-slate-100' : 'bg-blue-50 text-slate-800 border-blue-100'
                      }`}>
                      {msg.text}
                    </div>
                  </div>
                ))}
                {(!selectedResult.transcript || selectedResult.transcript.length === 0) && (
                  <p className="text-xs text-slate-400 text-center py-4">{t('dashboard.noData')}</p>
                )}
              </div>
              <div className="p-3 border-t border-slate-100 bg-slate-50 shrink-0 text-center">
                <button className="text-xs text-blue-600 font-bold hover:underline">{t('dashboard.showAllLogs')}</button>
              </div>
            </div>

            <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm shrink-0">
              <h3 className="text-sm font-bold text-slate-800 mb-4 flex items-center gap-2">
                <BarChart3 size={16} /> {t('dashboard.keyPoints')}
              </h3>
              <div className="space-y-3">
                {/* New format - Core Insight, Pain Points, Improvement Ideas */}
                {selectedResult.detailedReport?.score !== undefined ? (
                  <>
                    {selectedResult.detailedReport?.core_insight && (
                      <div className="flex gap-2 items-start text-xs">
                        <span className="mt-1 w-1.5 h-1.5 rounded-full shrink-0 bg-rose-500" />
                        <span className="text-slate-600 leading-snug">{selectedResult.detailedReport.core_insight}</span>
                      </div>
                    )}
                    {selectedResult.detailedReport?.pain_points && (
                      <div className="flex gap-2 items-start text-xs">
                        <span className="mt-1 w-1.5 h-1.5 rounded-full shrink-0 bg-amber-500" />
                        <span className="text-slate-600 leading-snug">{selectedResult.detailedReport.pain_points}</span>
                      </div>
                    )}
                    {selectedResult.detailedReport?.improvement_ideas && (
                      <div className="flex gap-2 items-start text-xs">
                        <span className="mt-1 w-1.5 h-1.5 rounded-full shrink-0 bg-blue-500" />
                        <span className="text-slate-600 leading-snug">{selectedResult.detailedReport.improvement_ideas}</span>
                      </div>
                    )}
                    {!selectedResult.detailedReport?.core_insight &&
                      !selectedResult.detailedReport?.pain_points &&
                      !selectedResult.detailedReport?.improvement_ideas && (
                        <p className="text-xs text-slate-400 text-center">{t('dashboard.noData')}</p>
                      )}
                  </>
                ) : (
                  /* Old format - use insights array */
                  <>
                    {(selectedResult.insights || []).map((insight, i) => (
                      <div key={i} className="flex gap-2 items-start text-xs">
                        <span className={`mt-1 w-1.5 h-1.5 rounded-full shrink-0 ${insight.type === 'positive' ? 'bg-emerald-500' :
                          insight.type === 'negative' ? 'bg-rose-500' : 'bg-blue-500'
                          }`} />
                        <span className="text-slate-600 leading-snug">{insight.text}</span>
                      </div>
                    ))}
                    {(!selectedResult.insights || selectedResult.insights.length === 0) && (
                      <p className="text-xs text-slate-400 text-center">{t('dashboard.noData')}</p>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Participants List View - Reference lines 496-534
  const ParticipantsListView = () => {
    const [searchQuery, setSearchQuery] = useState('');

    // Use real candidates from useCandidates hook (with null check)
    const filteredCandidates = (candidates || []).filter(
      (c) =>
        c.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        c.email?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
      <div className={`space-y-6 ${viewAnimationClass}`}>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-slate-800">{t('dashboard.intervieweeManagement')}</h2>
            <p className="text-sm text-slate-500">{t('dashboard.intervieweeManagementSubtitle')}</p>
          </div>
          <button
            onClick={() => setIsCreateCandidateModalOpen(true)}
            className="flex items-center gap-2 bg-slate-900 text-white px-4 py-2.5 rounded-lg text-sm font-bold hover:bg-slate-800"
          >
            <UserPlus size={18} /> {t('dashboard.registerNew')}
          </button>
        </div>

        <div className="flex gap-3">
          <div className="flex-1 relative">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('dashboard.searchPlaceholder')}
              className="w-full pl-12 pr-4 py-3 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <button className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-600 hover:bg-slate-50">
            <Filter size={16} /> {t('dashboard.filter')}
          </button>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left w-12">
                  <input type="checkbox" className="rounded border-slate-300" />
                </th>
                <th className="px-4 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">{t('dashboard.nameEmail')}</th>
                <th className="px-4 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">{t('dashboard.attributes')}</th>
                <th className="px-4 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">
                  <div className="flex items-center gap-1"><Brain size={12} /> {t('dashboard.aiTraits')}</div>
                </th>
                <th className="px-4 py-3 text-center text-xs font-bold text-slate-500 uppercase tracking-wider">{t('dashboard.projectCount')}</th>
                <th className="px-4 py-3 text-right text-xs font-bold text-slate-500 uppercase tracking-wider">{t('dashboard.action')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {candidatesLoading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" />
                    <p className="text-sm text-slate-400 mt-2">{t('dashboard.loading')}</p>
                  </td>
                </tr>
              ) : filteredCandidates.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center">
                    <Users className="w-8 h-8 mx-auto text-slate-300 mb-2" />
                    <p className="text-sm text-slate-400">{t('dashboard.noData')}</p>
                  </td>
                </tr>
              ) : filteredCandidates.map((candidate) => {
                const candidateIdStr = String(candidate.id);
                return (
                  <tr key={candidate.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedCandidates.includes(candidateIdStr)}
                        onChange={() =>
                          setSelectedCandidates((prev) =>
                            prev.includes(candidateIdStr) ? prev.filter((c) => c !== candidateIdStr) : [...prev, candidateIdStr]
                          )
                        }
                        className="rounded border-slate-300"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-slate-200 flex items-center justify-center text-sm font-bold text-slate-600">
                          {candidate.name?.charAt(0) || '?'}
                        </div>
                        <div>
                          <div className="font-medium text-sm text-slate-800">{candidate.name || '-'}</div>
                          <div className="text-xs text-slate-400">{candidate.email || '-'}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1 flex-wrap">
                        {(candidate.attributes || []).map((attr, i) => (
                          <span key={i} className="px-2 py-0.5 bg-slate-100 text-slate-600 text-[10px] rounded">{attr}</span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-xs font-medium text-slate-600">{candidate.analyzedTraits?.personality || '-'}</div>
                      <div className="flex gap-1 flex-wrap mt-1">
                        {(candidate.analyzedTraits?.interests || []).map((interest, i) => (
                          <span key={i} className="text-[10px] text-violet-600">#{interest}</span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="font-bold text-sm text-slate-700">{candidate.projectCount || 0}</span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg">
                        <MoreHorizontal size={16} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {selectedCandidates.length > 0 && (
          <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-slate-900 text-white px-6 py-3 rounded-full shadow-xl flex items-center gap-4 z-50">
            <span className="text-sm">{t('dashboard.selected', { count: selectedCandidates.length })}</span>
            <button className="bg-blue-600 px-4 py-1.5 rounded-full text-xs font-bold hover:bg-blue-700">
              {t('dashboard.addToProject')}
            </button>
          </div>
        )}
      </div>
    );
  };

  // Account Settings Component (extracted for cleaner state management)
  const AccountSettings = ({
    t,
    language,
    user,
    interviewerName,
    updateUserSettings,
    userSettingsLoading,
    handleLogout,
  }: {
    t: (key: string) => string;
    language: string;
    user: { displayName?: string | null; email?: string | null } | null;
    interviewerName: string;
    updateUserSettings: (settings: { interviewerName?: string; displayName?: string }) => Promise<void>;
    userSettingsLoading: boolean;
    handleLogout: () => void;
  }) => {
    const [localDisplayName, setLocalDisplayName] = useState(user?.displayName || '');
    const [localInterviewerName, setLocalInterviewerName] = useState(interviewerName);
    const [saving, setSaving] = useState(false);
    const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

    // Update local state when props change
    useEffect(() => {
      setLocalInterviewerName(interviewerName);
    }, [interviewerName]);

    useEffect(() => {
      setLocalDisplayName(user?.displayName || '');
    }, [user?.displayName]);

    const handleSave = async () => {
      setSaving(true);
      setSaveMessage(null);

      try {
        await updateUserSettings({
          displayName: localDisplayName,
          interviewerName: localInterviewerName || '吉松',
        });
        setSaveMessage({
          type: 'success',
          text: language === 'ja' ? t('dashboard.settingsSaveSuccess') : t('dashboard.settingsSaveSuccess'),
        });
        // Clear success message after 3 seconds
        setTimeout(() => setSaveMessage(null), 3000);
      } catch (error) {
        console.error('Failed to save settings:', error);
        setSaveMessage({
          type: 'error',
          text: language === 'ja' ? t('dashboard.settingsSaveError') : t('dashboard.settingsSaveError'),
        });
      } finally {
        setSaving(false);
      }
    };

    return (
      <div className="space-y-6">
        <h3 className="text-sm font-bold text-slate-800 pb-2 border-b border-slate-100">{t('dashboard.profile')}</h3>
        <div className="grid grid-cols-1 gap-6 max-w-lg">
          <div>
            <label className="block text-xs font-bold text-slate-700 mb-2">{t('dashboard.name')}</label>
            <input
              type="text"
              value={localDisplayName}
              onChange={(e) => setLocalDisplayName(e.target.value)}
              placeholder={user?.displayName || ''}
              className="w-full p-2.5 text-xs border border-slate-200 rounded-lg bg-white focus:outline-none focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-700 mb-2">{t('dashboard.email')}</label>
            <input
              type="email"
              value={user?.email || ''}
              disabled
              className="w-full p-2.5 text-xs border border-slate-200 rounded-lg bg-slate-100 text-slate-500 cursor-not-allowed"
            />
            <p className="text-[10px] text-slate-400 mt-1">{t('dashboard.emailChangeDisabled')}</p>
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-700 mb-2">{t('dashboard.interviewerName')}</label>
            <input
              type="text"
              value={localInterviewerName}
              onChange={(e) => setLocalInterviewerName(e.target.value)}
              placeholder="吉松"
              disabled={userSettingsLoading}
              className="w-full p-2.5 text-xs border border-slate-200 rounded-lg bg-white focus:outline-none focus:border-blue-500 disabled:bg-slate-100 disabled:cursor-not-allowed"
            />
            <p className="text-[10px] text-slate-400 mt-1">{t('dashboard.interviewerNameHint')}</p>
          </div>
        </div>
        <div className="pt-4 border-t border-slate-100 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <button
              onClick={handleSave}
              disabled={saving || userSettingsLoading}
              className="px-4 py-2 text-xs font-bold text-white bg-slate-900 rounded-lg hover:bg-slate-800 transition-colors disabled:bg-slate-400 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {saving && <Loader2 size={14} className="animate-spin" />}
              {t('dashboard.saveChanges')}
            </button>
            {saveMessage && (
              <span className={`text-xs font-medium ${saveMessage.type === 'success' ? 'text-green-600' : 'text-rose-600'}`}>
                {saveMessage.type === 'success' && <Check size={14} className="inline mr-1" />}
                {saveMessage.type === 'error' && <AlertCircle size={14} className="inline mr-1" />}
                {saveMessage.text}
              </span>
            )}
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 text-xs font-bold text-rose-600 hover:bg-rose-50 px-3 py-2 rounded transition-colors w-fit"
          >
            <LogOut size={14} /> {t('dashboard.logout')}
          </button>
        </div>
      </div>
    );
  };

  // Settings View - Reference lines 536-632
  const SettingsView = () => {
    // settingsTab state is now lifted to parent component for external navigation

    return (
      <div className={`space-y-6 ${viewAnimationClass}`}>
        <h2 className="text-xl font-bold text-slate-800">{t('dashboard.settingsTitle')}</h2>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Settings Nav */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              <nav className="flex flex-col p-2 space-y-1">
                <button
                  onClick={() => setSettingsTab('general')}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg text-xs font-bold transition-colors text-left ${settingsTab === 'general' ? 'bg-blue-50 text-blue-600' : 'text-slate-600 hover:bg-slate-50'
                    }`}
                >
                  <Settings size={16} /> {t('dashboard.general')}
                </button>
                <button
                  onClick={() => setSettingsTab('account')}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg text-xs font-bold transition-colors text-left ${settingsTab === 'account' ? 'bg-blue-50 text-blue-600' : 'text-slate-600 hover:bg-slate-50'
                    }`}
                >
                  <User size={16} /> {t('dashboard.account')}
                </button>
              </nav>
            </div>
          </div>

          {/* Settings Content */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8 min-h-[500px]">
              {settingsTab === 'general' && (
                <div className="space-y-6">
                  <h3 className="text-sm font-bold text-slate-800 pb-2 border-b border-slate-100">{t('dashboard.general')}</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-xs font-bold text-slate-700 mb-2">{t('dashboard.languageSettings')}</label>
                      <select
                        value={language}
                        onChange={(e) => i18n.changeLanguage(e.target.value)}
                        className="w-full max-w-xs p-2.5 text-xs border border-slate-200 rounded-lg bg-slate-50 focus:outline-none focus:border-blue-500"
                      >
                        <option value="ja">日本語</option>
                        <option value="en">English</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-700 mb-2">{t('dashboard.timezone')}</label>
                      <select className="w-full max-w-xs p-2.5 text-xs border border-slate-200 rounded-lg bg-slate-50 focus:outline-none focus:border-blue-500">
                        <option>(GMT+09:00) Tokyo, Osaka, Sapporo</option>
                        <option>(GMT+00:00) UTC</option>
                        <option>(GMT-08:00) Pacific Time (US & Canada)</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}

              {settingsTab === 'account' && (
                <AccountSettings
                  t={t}
                  language={language}
                  user={user}
                  interviewerName={interviewerName}
                  updateUserSettings={updateUserSettings}
                  userSettingsLoading={userSettingsLoading}
                  handleLogout={handleLogout}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ==================== MODALS ====================

  // Create Design Modal
  const CreateDesignModal = () => {
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [type, setType] = useState<'Discovery' | 'Validation' | 'Pricing'>('Discovery');
    const [creating, setCreating] = useState(false);

    const handleCreate = async () => {
      if (!name.trim()) {
        alert(language === 'ja' ? 'デザイン名を入力してください' : 'Please enter a design name');
        return;
      }
      setCreating(true);
      try {
        const newDesign = await createDesign({
          name: name.trim(),
          description: description.trim(),
          type,
        });
        setIsCreateDesignModalOpen(false);
        setName('');
        setDescription('');
        // Select the new design and go to template editor
        if (newDesign) {
          setSelectedDesignIdState(newDesign.id);
          setViewMode('template');
        }
      } catch (error) {
        console.error('Failed to create design:', error);
        alert(language === 'ja' ? 'デザインの作成に失敗しました' : 'Failed to create design');
      } finally {
        setCreating(false);
      }
    };

    if (!isCreateDesignModalOpen) return null;

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-xl w-full max-w-md animate-in zoom-in-95 duration-200">
          <div className="p-5 border-b border-slate-100">
            <h3 className="text-lg font-bold text-slate-800">{language === 'ja' ? '新しいデザインを作成' : 'Create New Design'}</h3>
          </div>
          <div className="p-5 space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                {language === 'ja' ? 'デザイン名' : 'Design Name'} *
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={language === 'ja' ? '例: ユーザー満足度調査' : 'e.g., User Satisfaction Survey'}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                {language === 'ja' ? '説明' : 'Description'}
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={language === 'ja' ? 'デザインの目的や内容を記述...' : 'Describe the purpose of this design...'}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                rows={3}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                {language === 'ja' ? 'タイプ' : 'Type'}
              </label>
              <select
                value={type}
                onChange={(e) => setType(e.target.value as 'Discovery' | 'Validation' | 'Pricing')}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="Discovery">{language === 'ja' ? '発見' : 'Discovery'}</option>
                <option value="Validation">{language === 'ja' ? '検証' : 'Validation'}</option>
                <option value="Pricing">{language === 'ja' ? '価格' : 'Pricing'}</option>
              </select>
            </div>
          </div>
          <div className="p-5 border-t border-slate-100 flex justify-end gap-3">
            <button
              onClick={() => {
                setIsCreateDesignModalOpen(false);
                setName('');
                setDescription('');
              }}
              className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
            >
              {language === 'ja' ? 'キャンセル' : 'Cancel'}
            </button>
            <button
              onClick={handleCreate}
              disabled={creating || !name.trim()}
              className="px-4 py-2 text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {creating && <Loader2 size={14} className="animate-spin" />}
              {language === 'ja' ? '作成' : 'Create'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Delete Design Confirmation Modal
  const DeleteDesignModal = () => {
    const handleDelete = async () => {
      if (!designToDelete) return;
      setDeletingDesign(true);
      try {
        await deleteDesign(designToDelete.id);
        // Refresh the designs list
        await refetchProject();
        setIsDeleteDesignModalOpen(false);
        setDesignToDelete(null);
        // If we were viewing this design, go back to list
        if (selectedDesignIdState === designToDelete.id) {
          setSelectedDesignIdState(null);
          setViewMode('list');
        }
      } catch (error) {
        console.error('Failed to delete design:', error);
        alert(language === 'ja' ? 'デザインの削除に失敗しました' : 'Failed to delete design');
      } finally {
        setDeletingDesign(false);
      }
    };

    if (!isDeleteDesignModalOpen || !designToDelete) return null;

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-xl w-full max-w-md animate-in zoom-in-95 duration-200">
          <div className="p-5 border-b border-slate-100">
            <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
              <AlertCircle size={20} className="text-red-500" />
              {language === 'ja' ? 'デザインを削除' : 'Delete Design'}
            </h3>
          </div>
          <div className="p-5">
            <p className="text-sm text-slate-600 mb-4">
              {language === 'ja'
                ? `「${designToDelete.name}」を削除してもよろしいですか？この操作は取り消せません。関連するすべての質問と回答も削除されます。`
                : `Are you sure you want to delete "${designToDelete.name}"? This action cannot be undone. All related questions and responses will also be deleted.`}
            </p>
            <div className="bg-red-50 border border-red-100 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <AlertCircle size={16} className="text-red-500 mt-0.5 shrink-0" />
                <div className="text-xs text-red-700">
                  <p className="font-medium mb-1">
                    {language === 'ja' ? 'この操作により以下が削除されます:' : 'This will permanently delete:'}
                  </p>
                  <ul className="list-disc list-inside space-y-0.5">
                    <li>{language === 'ja' ? 'デザイン設定' : 'Design settings'}</li>
                    <li>{language === 'ja' ? `${designToDelete.questions || 0} 件の質問` : `${designToDelete.questions || 0} question(s)`}</li>
                    <li>{language === 'ja' ? `${designToDelete.totalCount || 0} 件のインタビュー結果` : `${designToDelete.totalCount || 0} interview result(s)`}</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
          <div className="p-5 border-t border-slate-100 flex justify-end gap-3">
            <button
              onClick={() => {
                setIsDeleteDesignModalOpen(false);
                setDesignToDelete(null);
              }}
              disabled={deletingDesign}
              className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-50"
            >
              {language === 'ja' ? 'キャンセル' : 'Cancel'}
            </button>
            <button
              onClick={handleDelete}
              disabled={deletingDesign}
              className="px-4 py-2 text-sm font-bold text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {deletingDesign && <Loader2 size={14} className="animate-spin" />}
              {language === 'ja' ? '削除する' : 'Delete'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Add Participant Modal
  const AddParticipantModal = () => {
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [adding, setAdding] = useState(false);

    // Get target design from real designs array
    const targetDesign = designs.find((d) => d.id === targetDesignId);

    // Get candidate IDs that are already participants for this design
    const existingParticipantCandidateIds = useMemo(() => {
      if (!targetDesignId) return new Set<string>();
      return new Set(
        participants
          .filter((p) => p.designId === targetDesignId)
          .map((p) => p.candidateId)
      );
    }, [participants, targetDesignId]);

    // Filter candidates: match search query AND exclude already-added participants
    const filteredCandidates = useMemo(() => {
      return candidates.filter(
        (c) =>
          // Search filter
          (c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            c.email.toLowerCase().includes(searchQuery.toLowerCase())) &&
          // Exclude candidates who are already participants for this design
          !existingParticipantCandidateIds.has(c.id)
      );
    }, [candidates, searchQuery, existingParticipantCandidateIds]);

    const handleAddParticipants = async () => {
      if (!targetDesignId || selectedIds.length === 0) return;
      setAdding(true);
      try {
        await addParticipants(targetDesignId, selectedIds);
        // Refresh participants list first, then close modal
        await refetchParticipants();
        setIsAddParticipantModalOpen(false);
        setTargetDesignId(null);
        setSelectedIds([]);
      } catch (error) {
        console.error('Failed to add participants:', error);
      } finally {
        setAdding(false);
      }
    };

    return (
      <div className="fixed inset-0 bg-slate-900/40 z-[60] flex items-center justify-center p-4 backdrop-blur-sm animate-in fade-in duration-200">
        <div className="bg-white w-full max-w-2xl rounded-xl shadow-2xl flex flex-col max-h-[80vh]">
          <div className="p-5 border-b border-slate-100 flex justify-between items-center">
            <h3 className="text-base font-bold text-slate-800">
              {t('dashboard.addParticipantTitle')}
              {targetDesign && (
                <span className="ml-2 text-xs font-normal text-slate-500 bg-slate-100 px-2 py-0.5 rounded">
                  to: {targetDesign.name}
                </span>
              )}
            </h3>
            <button
              onClick={() => {
                setIsAddParticipantModalOpen(false);
                setTargetDesignId(null);
              }}
              className="text-slate-400 hover:text-slate-600 p-1 hover:bg-slate-50 rounded"
            >
              <X size={20} />
            </button>
          </div>

          <div className="p-4 border-b border-slate-100 flex gap-3">
            <div className="flex-1 relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('dashboard.searchPlaceholder')}
                className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <button className="flex items-center gap-1 px-3 py-2 border border-slate-200 rounded-lg text-xs text-slate-600 hover:bg-slate-50">
              <Filter size={14} /> {t('dashboard.filter')}
            </button>
          </div>

          <div className="max-h-80 overflow-y-auto">
            <table className="w-full">
              <thead className="bg-slate-50 sticky top-0">
                <tr>
                  <th className="px-4 py-2 text-left w-10" />
                  <th className="px-4 py-2 text-left text-[10px] font-bold text-slate-500 uppercase">{t('dashboard.nameEmail')}</th>
                  <th className="px-4 py-2 text-left text-[10px] font-bold text-slate-500 uppercase">{t('dashboard.attributes')}</th>
                  <th className="px-4 py-2 text-left text-[10px] font-bold text-slate-500 uppercase">{t('dashboard.candidateTags')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {candidatesLoading ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-8 text-center">
                      <Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-400" />
                    </td>
                  </tr>
                ) : filteredCandidates.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-8 text-center text-sm text-slate-400">
                      {t('dashboard.noData')}
                    </td>
                  </tr>
                ) : filteredCandidates.map((candidate) => {
                  const candidateIdStr = String(candidate.id);
                  return (
                    <tr
                      key={candidate.id}
                      className={`hover:bg-slate-50 cursor-pointer ${selectedIds.includes(candidateIdStr) ? 'bg-blue-50' : ''}`}
                      onClick={() =>
                        setSelectedIds((prev) =>
                          prev.includes(candidateIdStr) ? prev.filter((i) => i !== candidateIdStr) : [...prev, candidateIdStr]
                        )
                      }
                    >
                      <td className="px-4 py-2">
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(candidateIdStr)}
                          onChange={() => { }}
                          className="rounded border-slate-300"
                        />
                      </td>
                      <td className="px-4 py-2">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-full bg-slate-200 flex items-center justify-center text-xs font-bold text-slate-600">
                            {candidate.name?.charAt(0) || '?'}
                          </div>
                          <div>
                            <div className="text-sm font-medium text-slate-700">{candidate.name || '-'}</div>
                            <div className="text-[10px] text-slate-400">{candidate.email || '-'}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-2">
                        <div className="flex gap-1 flex-wrap">
                          {(candidate.attributes || []).map((attr, i) => (
                            <span key={i} className="px-1.5 py-0.5 bg-slate-100 text-slate-600 text-[10px] rounded">{attr}</span>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-2">
                        <div className="flex gap-1 flex-wrap">
                          {(candidate.tags || []).map((tag, i) => (
                            <span key={i} className="px-1.5 py-0.5 bg-violet-100 text-violet-600 text-[10px] rounded">{tag}</span>
                          ))}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="p-4 border-t border-slate-100 flex justify-between items-center">
            <span className="text-xs text-slate-500">{t('dashboard.selected', { count: selectedIds.length })}</span>
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setIsAddParticipantModalOpen(false);
                  setTargetDesignId(null);
                }}
                className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800"
                disabled={adding}
              >
                {t('dashboard.cancel')}
              </button>
              <button
                onClick={handleAddParticipants}
                disabled={selectedIds.length === 0 || adding}
                className="bg-slate-900 text-white px-5 py-2 rounded-lg text-sm font-bold hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {adding && <Loader2 size={14} className="animate-spin" />}
                {t('dashboard.addParticipant')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Export Modal
  const ExportModal = () => {
    const [selectedFormat, setSelectedFormat] = useState<'markdown' | 'json' | 'csv'>('markdown');
    const [includeTranscripts, setIncludeTranscripts] = useState(true);
    const [includeAnalysis, setIncludeAnalysis] = useState(true);
    const [splitFiles, setSplitFiles] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [exportError, setExportError] = useState<string | null>(null);

    const handleExport = async () => {
      if (!selectedProjectId) {
        setExportError(isJapanese ? 'プロジェクトが選択されていません' : 'No project selected');
        return;
      }

      setIsExporting(true);
      setExportError(null);

      try {
        // Fetch questions for all designs in parallel
        const designsWithQuestions = await Promise.all(
          (designs || []).map(async (design) => {
            try {
              const { questions } = await designsService.getQuestions(design.id);
              return {
                ...design,
                questionsList: questions || [],
              };
            } catch (err) {
              console.warn(`Failed to fetch questions for design ${design.id}:`, err);
              return {
                ...design,
                questionsList: [],
              };
            }
          })
        );

        const exportData = {
          project: selectedProject,
          designs: designsWithQuestions,
          participants: participants,
          results: results,
          insights: insights,
          exportedAt: new Date().toISOString(),
        };

        let content: string;
        let filename: string;
        let mimeType: string;

        const projectName = selectedProject?.name?.replace(/[^a-zA-Z0-9]/g, '_') || 'project';
        const timestamp = new Date().toISOString().split('T')[0];

        if (selectedFormat === 'json') {
          content = JSON.stringify(exportData, null, 2);
          filename = `${projectName}_export_${timestamp}.json`;
          mimeType = 'application/json';
        } else if (selectedFormat === 'csv') {
          // Create CSV from results
          const csvRows: string[] = [];
          csvRows.push('Participant,Design,Date,Duration,Summary');

          (results || []).forEach((result) => {
            const participant = (participants || []).find(p => p.id === result.participantId);
            const design = (designs || []).find(d => d.id === result.designId);
            csvRows.push([
              participant?.name || participant?.candidate?.name || 'Unknown',
              design?.name || 'Unknown',
              result.completedAt ? new Date(result.completedAt as unknown as string).toLocaleDateString() : '',
              result.duration ? `${result.duration} min` : '',
              (result.analysis?.summary || '').replace(/,/g, ';').replace(/\n/g, ' '),
            ].map(v => `"${v}"`).join(','));
          });

          content = csvRows.join('\n');
          filename = `${projectName}_export_${timestamp}.csv`;
          mimeType = 'text/csv';
        } else {
          // Markdown format
          const lines: string[] = [];
          lines.push(`# ${selectedProject?.name || 'Project'} Export`);
          lines.push(`\nExported: ${new Date().toLocaleString()}\n`);

          lines.push(`## Project Overview`);
          lines.push(`- **Name:** ${selectedProject?.name}`);
          lines.push(`- **Category:** ${selectedProject?.category}`);
          lines.push(`- **Status:** ${selectedProject?.status}`);
          lines.push(`- **Description:** ${selectedProject?.description || 'N/A'}\n`);

          lines.push(`## Interview Designs (${designsWithQuestions.length})`);
          designsWithQuestions.forEach((design, idx) => {
            lines.push(`\n### ${idx + 1}. ${design.name}`);
            lines.push(`- **Type:** ${design.type}`);
            lines.push(`- **Description:** ${design.description || 'N/A'}`);
            lines.push(`- **Status:** ${design.status}`);
            lines.push(`- **Question Count:** ${design.questionsList?.length ?? design.questionCount ?? 0}`);
            lines.push(`- **Completed Interviews:** ${design.completedCount ?? 0}/${design.totalCount ?? 0}`);
            lines.push(`- **Interviewer Name:** ${design.interviewerName || 'N/A'}`);
            lines.push(`- **Tone:** ${design.tone || 'N/A'}`);
            if (design.avgDuration) {
              lines.push(`- **Average Duration:** ${design.avgDuration}`);
            }

            // Include all questions for each design
            if (design.questionsList && design.questionsList.length > 0) {
              lines.push(`\n#### Questions`);
              design.questionsList.forEach((q, qIdx) => {
                lines.push(`${qIdx + 1}. **[${q.type}]** ${q.text}${q.required ? ' *(required)*' : ''}`);
              });
            }
          });

          lines.push(`\n## Participants (${(participants || []).length})`);
          (participants || []).forEach((p, idx) => {
            const pName = p.name || p.candidate?.name || 'Unknown';
            lines.push(`${idx + 1}. ${pName} - ${p.status}`);
          });

          if (includeAnalysis && insights) {
            lines.push(`\n## Insights`);
            lines.push(`\n### Executive Summary`);
            lines.push(insights.executiveSummary || 'No summary available');

            if (insights.keyTakeaways && insights.keyTakeaways.length > 0) {
              lines.push(`\n### Key Takeaways`);
              insights.keyTakeaways.forEach((t) => {
                lines.push(`- **[${t.type}]** ${t.title}: ${t.desc}`);
              });
            }

            if (insights.recommendations && insights.recommendations.length > 0) {
              lines.push(`\n### Recommendations`);
              insights.recommendations.forEach((r, idx) => {
                lines.push(`${idx + 1}. ${r}`);
              });
            }
          }

          if (includeTranscripts && results && results.length > 0) {
            lines.push(`\n## Interview Results (${results.length})`);
            results.forEach((result, idx) => {
              const participant = (participants || []).find(p => p.id === result.participantId);
              lines.push(`\n### Result ${idx + 1}: ${participant?.name || participant?.candidate?.name || 'Unknown'}`);
              lines.push(`- Duration: ${result.duration ? `${result.duration} min` : 'N/A'}`);
              lines.push(`- Summary: ${result.analysis?.summary || 'N/A'}`);

              if (result.transcript && result.transcript.length > 0) {
                lines.push(`\n#### Transcript`);
                result.transcript.forEach((t) => {
                  lines.push(`**${t.speaker}:** ${t.text}`);
                });
              }
            });
          }

          content = lines.join('\n');
          filename = `${projectName}_export_${timestamp}.md`;
          mimeType = 'text/markdown';
        }

        // Create and trigger download
        const blob = new Blob([content], { type: mimeType });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        setIsExportModalOpen(false);
      } catch (err) {
        console.error('Export failed:', err);
        setExportError(err instanceof Error ? err.message : (isJapanese ? 'エクスポートに失敗しました' : 'Export failed'));
      } finally {
        setIsExporting(false);
      }
    };

    return (
      <div className="fixed inset-0 bg-slate-900/40 z-[70] flex items-center justify-center p-4 backdrop-blur-sm animate-in fade-in duration-200">
        <div className="bg-white w-full max-w-md rounded-xl shadow-2xl flex flex-col">
          <div className="p-5 border-b border-slate-100 flex justify-between items-center">
            <h3 className="text-base font-bold text-slate-800 flex items-center gap-2">
              <Download size={18} className="text-blue-500" />
              {t('dashboard.exportSettings')}
            </h3>
            <button onClick={() => setIsExportModalOpen(false)} className="text-slate-400 hover:text-slate-600 p-1 hover:bg-slate-50 rounded">
              <X size={20} />
            </button>
          </div>

          <div className="p-6 space-y-6">
            {exportError && (
              <div className="p-3 bg-red-50 border border-red-100 rounded-lg flex gap-2 items-center text-sm text-red-600">
                <AlertCircle size={16} />
                {exportError}
              </div>
            )}

            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">{t('dashboard.exportFormat')}</label>
              <div className="grid grid-cols-3 gap-2">
                {([
                  { value: 'markdown' as const, label: t('dashboard.exportMarkdown') },
                  { value: 'json' as const, label: t('dashboard.exportJson') },
                  { value: 'csv' as const, label: t('dashboard.exportCsv') },
                ]).map((format) => (
                  <button
                    key={format.value}
                    onClick={() => setSelectedFormat(format.value)}
                    className={`px-3 py-2.5 rounded-lg border-2 text-xs font-bold transition-colors ${selectedFormat === format.value
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-slate-200 hover:border-slate-300'
                      }`}
                  >
                    {format.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-3">
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">{t('dashboard.exportContent')}</label>
              {[
                { checked: includeTranscripts, onChange: setIncludeTranscripts, label: t('dashboard.includeTranscripts') },
                { checked: includeAnalysis, onChange: setIncludeAnalysis, label: t('dashboard.includeAnalysis') },
                { checked: splitFiles, onChange: setSplitFiles, label: t('dashboard.splitFiles') },
              ].map((option, idx) => (
                <label key={idx} className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={option.checked}
                    onChange={(e) => option.onChange(e.target.checked)}
                    className="rounded border-slate-300"
                  />
                  <span className="text-sm text-slate-700">{option.label}</span>
                </label>
              ))}
            </div>

            <div className="p-4 bg-amber-50 border border-amber-100 rounded-lg flex gap-3">
              <Lightbulb size={18} className="text-amber-500 shrink-0 mt-0.5" />
              <p className="text-xs text-amber-800">{t('dashboard.notebookLmHint')}</p>
            </div>

            <div className="flex justify-end gap-2">
              <button onClick={() => setIsExportModalOpen(false)} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800">
                {t('dashboard.cancel')}
              </button>
              <button
                onClick={handleExport}
                disabled={isExporting}
                className="bg-slate-900 text-white px-5 py-2 rounded-lg text-sm font-bold hover:bg-slate-800 flex items-center gap-2 disabled:opacity-50"
              >
                {isExporting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                    {t('dashboard.exporting')}
                  </>
                ) : (
                  <>
                    <Download size={16} /> {t('dashboard.export')}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Loading Modal - shown when fetching data
  const LoadingModal = () => (
    <div className="fixed inset-0 bg-slate-900/30 z-[100] flex items-center justify-center backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white rounded-2xl shadow-2xl p-8 flex flex-col items-center gap-4 min-w-[280px]">
        <div className="relative">
          <div className="w-16 h-16 rounded-full border-4 border-slate-100 border-t-blue-500 animate-spin" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-violet-500 flex items-center justify-center">
              <Loader2 size={20} className="text-white animate-spin" style={{ animationDirection: 'reverse' }} />
            </div>
          </div>
        </div>
        <div className="text-center">
          <p className="text-sm font-bold text-slate-700">{getLoadingMessage()}</p>
          <p className="text-xs text-slate-400 mt-1">
            {isJapanese ? 'しばらくお待ちください' : 'Please wait a moment'}
          </p>
        </div>
      </div>
    </div>
  );

  // Create Project Modal
  const CreateProjectModal = () => {
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [category, setCategory] = useState<'Product' | 'Marketing' | 'CS'>('Product');
    const [isCreating, setIsCreating] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleCreate = async () => {
      if (!name.trim()) {
        setError(t('dashboard.projectName') + ' is required');
        return;
      }

      setIsCreating(true);
      setError(null);

      try {
        const newProject = await createProject({
          name: name.trim(),
          description: description.trim(),
          category,
        });
        setIsCreateProjectModalOpen(false);
        // Navigate to the new project
        setSelectedProjectId(newProject.id);
        setViewMode('detail');
      } catch (err) {
        console.error('Failed to create project:', err);
        setError(err instanceof Error ? err.message : 'Failed to create project');
      } finally {
        setIsCreating(false);
      }
    };

    const categories: Array<{ value: 'Product' | 'Marketing' | 'CS'; label: string }> = [
      { value: 'Product', label: t('dashboard.categoryProduct') },
      { value: 'Marketing', label: t('dashboard.categoryMarketing') },
      { value: 'CS', label: t('dashboard.categoryCS') },
    ];

    return (
      <div className="fixed inset-0 bg-slate-900/40 z-[70] flex items-center justify-center p-4 backdrop-blur-sm animate-in fade-in duration-200">
        <div className="bg-white w-full max-w-md rounded-xl shadow-2xl flex flex-col">
          <div className="p-5 border-b border-slate-100 flex justify-between items-center">
            <h3 className="text-base font-bold text-slate-800 flex items-center gap-2">
              <Layers size={18} className="text-blue-500" />
              {t('dashboard.createProjectTitle')}
            </h3>
            <button
              onClick={() => setIsCreateProjectModalOpen(false)}
              className="text-slate-400 hover:text-slate-600 p-1 hover:bg-slate-50 rounded"
            >
              <X size={20} />
            </button>
          </div>

          <div className="p-6 space-y-5">
            {error && (
              <div className="p-3 bg-red-50 border border-red-100 rounded-lg flex gap-2 items-center text-sm text-red-600">
                <AlertCircle size={16} />
                {error}
              </div>
            )}

            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                {t('dashboard.projectName')} *
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('dashboard.projectNamePlaceholder')}
                className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-colors"
                autoFocus
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                {t('dashboard.projectDescription')}
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t('dashboard.projectDescriptionPlaceholder')}
                rows={3}
                className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-colors resize-none"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">
                {t('dashboard.projectCategory')}
              </label>
              <div className="grid grid-cols-3 gap-2">
                {categories.map((cat) => (
                  <button
                    key={cat.value}
                    onClick={() => setCategory(cat.value)}
                    className={`px-3 py-2.5 rounded-lg border-2 text-xs font-bold transition-colors ${category === cat.value
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-slate-200 hover:border-slate-300'
                      }`}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={() => setIsCreateProjectModalOpen(false)}
                className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800"
              >
                {t('dashboard.cancel')}
              </button>
              <button
                onClick={handleCreate}
                disabled={isCreating || !name.trim()}
                className="bg-slate-900 text-white px-5 py-2 rounded-lg text-sm font-bold hover:bg-slate-800 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isCreating ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                    {t('dashboard.creating')}
                  </>
                ) : (
                  <>
                    <Plus size={16} /> {t('dashboard.createProject')}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Create Candidate Modal
  const CreateCandidateModal = () => {
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [attributes, setAttributes] = useState('');
    const [isCreating, setIsCreating] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleCreate = async () => {
      if (!name.trim()) {
        setError(isJapanese ? '名前は必須です' : 'Name is required');
        return;
      }
      if (!email.trim()) {
        setError(isJapanese ? 'メールアドレスは必須です' : 'Email is required');
        return;
      }
      // Basic email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email.trim())) {
        setError(isJapanese ? '有効なメールアドレスを入力してください' : 'Please enter a valid email address');
        return;
      }

      setIsCreating(true);
      setError(null);

      try {
        await createCandidate({
          name: name.trim(),
          email: email.trim(),
          attributes: attributes.trim() ? attributes.split(',').map(a => a.trim()) : [],
        });
        setIsCreateCandidateModalOpen(false);
        // Refetch candidates to show the new one
        refetchCandidates();
      } catch (err) {
        console.error('Failed to create candidate:', err);
        setError(err instanceof Error ? err.message : (isJapanese ? '作成に失敗しました' : 'Failed to create candidate'));
      } finally {
        setIsCreating(false);
      }
    };

    return (
      <div className="fixed inset-0 bg-slate-900/40 z-[70] flex items-center justify-center p-4 backdrop-blur-sm animate-in fade-in duration-200">
        <div className="bg-white w-full max-w-md rounded-xl shadow-2xl flex flex-col">
          <div className="p-5 border-b border-slate-100 flex justify-between items-center">
            <h3 className="text-base font-bold text-slate-800 flex items-center gap-2">
              <UserPlus size={18} className="text-blue-500" />
              {isJapanese ? '新規候補者登録' : 'Register New Candidate'}
            </h3>
            <button
              onClick={() => setIsCreateCandidateModalOpen(false)}
              className="text-slate-400 hover:text-slate-600 p-1 hover:bg-slate-50 rounded"
            >
              <X size={20} />
            </button>
          </div>

          <div className="p-6 space-y-5">
            {error && (
              <div className="p-3 bg-red-50 border border-red-100 rounded-lg flex gap-2 items-center text-sm text-red-600">
                <AlertCircle size={16} />
                {error}
              </div>
            )}

            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                {isJapanese ? '名前' : 'Name'} *
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={isJapanese ? '例: 山田太郎' : 'e.g. John Doe'}
                className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-colors"
                autoFocus
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                {isJapanese ? 'メールアドレス' : 'Email'} *
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={isJapanese ? '例: yamada@example.com' : 'e.g. john@example.com'}
                className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-colors"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                {isJapanese ? '属性（カンマ区切り）' : 'Attributes (comma separated)'}
              </label>
              <input
                type="text"
                value={attributes}
                onChange={(e) => setAttributes(e.target.value)}
                placeholder={isJapanese ? '例: 30代, 会社員, 東京' : 'e.g. 30s, Engineer, Tokyo'}
                className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-colors"
              />
              <p className="text-xs text-slate-400 mt-1">
                {isJapanese ? '複数の属性はカンマで区切ってください' : 'Separate multiple attributes with commas'}
              </p>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={() => setIsCreateCandidateModalOpen(false)}
                className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800"
              >
                {t('dashboard.cancel')}
              </button>
              <button
                onClick={handleCreate}
                disabled={isCreating || !name.trim() || !email.trim()}
                className="bg-slate-900 text-white px-5 py-2 rounded-lg text-sm font-bold hover:bg-slate-800 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isCreating ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    {isJapanese ? '登録中...' : 'Creating...'}
                  </>
                ) : (
                  <>
                    <UserPlus size={16} /> {isJapanese ? '登録' : 'Register'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ==================== MAIN RENDER ====================

  return (
    <div className="bg-[#F8FAFC] min-h-screen flex font-sans text-slate-800 selection:bg-blue-100 selection:text-blue-900">
      {/* Sidebar (Desktop) - Reference lines 1884-1923 */}
      <aside className="fixed top-0 left-0 h-screen w-20 bg-white border-r border-slate-200 flex flex-col items-center py-6 z-50 hidden md:flex">
        <div className="w-10 h-10 bg-slate-900 text-white rounded-xl flex items-center justify-center font-bold text-xl mb-10 shadow-sm">
          H
        </div>

        <nav className="flex flex-col gap-4 w-full px-3">
          <button
            onClick={() => { setViewMode('list'); setActiveTab('projects'); }}
            className={`w-10 h-10 mx-auto rounded-lg flex items-center justify-center transition-all ${activeTab === 'projects' ? 'bg-slate-100 text-slate-900' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-50'
              }`}
            title={isJapanese ? '分析' : 'Analytics'}
          >
            <Layout size={20} strokeWidth={2} />
          </button>
          <button
            onClick={() => { setViewMode('candidates_list'); setActiveTab('candidates'); }}
            className={`w-10 h-10 mx-auto rounded-lg flex items-center justify-center transition-all ${activeTab === 'candidates' ? 'bg-slate-100 text-slate-900' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-50'
              }`}
            title={isJapanese ? 'ユーザー管理' : 'User Management'}
          >
            <Users size={20} strokeWidth={2} />
          </button>
        </nav>

        <div className="mt-auto px-3 space-y-4 flex flex-col items-center">
          <button
            onClick={toggleLanguage}
            className="w-10 h-10 rounded-lg flex items-center justify-center text-slate-500 font-bold hover:bg-slate-50 transition-all text-xs border border-transparent hover:border-slate-200"
            title="Switch Language"
          >
            {language === 'ja' ? 'EN' : 'JA'}
          </button>
          <button
            onClick={() => { setViewMode('settings'); setActiveTab('settings'); }}
            className={`w-10 h-10 rounded-lg flex items-center justify-center transition-all ${activeTab === 'settings' ? 'bg-slate-100 text-slate-900' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-50'
              }`}
            title={t('dashboard.settings')}
          >
            <Settings size={20} strokeWidth={2} />
          </button>
          <Link
            to={`/${locale}`}
            className="w-10 h-10 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-50 transition-all"
            title={t('dashboard.backToInterview')}
          >
            <ArrowRight size={20} strokeWidth={2} className="rotate-180" />
          </Link>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 md:ml-20 min-w-0">
        {/* Header - Reference lines 1927-1937 */}
        <header className="h-16 bg-white/80 backdrop-blur-md border-b border-slate-200 sticky top-0 z-40 px-6 flex items-center justify-between">
          <h1 className="text-lg font-bold text-slate-800 flex items-center gap-2">
            HearO Research <span className="text-slate-300 font-light">|</span> <span className="text-sm font-medium text-slate-500">{isJapanese ? '管理ダッシュボード' : 'Admin Dashboard'}</span>
          </h1>
          <div className="flex items-center gap-3">
            <button
              onClick={() => {
                setSettingsTab('account');
                setViewMode('settings');
                setActiveTab('settings');
              }}
              className="w-8 h-8 rounded bg-slate-100 border border-slate-200 flex items-center justify-center text-xs font-bold text-slate-600 hover:bg-slate-200 hover:border-slate-300 transition-colors cursor-pointer"
              title={t('dashboard.account')}
            >
              {userInitials}
            </button>
          </div>
        </header>

        {/* Dynamic View Area */}
        <div className="p-6 md:p-10 max-w-7xl mx-auto">
          {viewMode === 'list' && <ProjectListView />}
          {viewMode === 'detail' && selectedProject && <ProjectDetailView />}
          {viewMode === 'template' && selectedProject && <TemplateEditorView />}
          {viewMode === 'result' && <ResultDetailView />}
          {viewMode === 'test_play' && <InterviewScreenView />}
          {(viewMode === 'project_insight' || viewMode === 'insight') && selectedProject && <ProjectInsightView />}
          {viewMode === 'participants_list' && <ParticipantsListView />}
          {viewMode === 'candidates_list' && <CandidateManagement />}
          {viewMode === 'settings' && <SettingsView />}
        </div>

        {/* Modals */}
        {isAddParticipantModalOpen && <AddParticipantModal />}
        {isExportModalOpen && <ExportModal />}
        {isCreateProjectModalOpen && <CreateProjectModal />}
        {isCreateDesignModalOpen && <CreateDesignModal />}
        {isCreateCandidateModalOpen && <CreateCandidateModal />}
        {isDeleteDesignModalOpen && <DeleteDesignModal />}

        {/* Global Loading Modal */}
        {isDataLoading && <LoadingModal />}
      </main>
    </div>
  );
}
